import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import styled, { useTheme } from 'styled-components';
/* [LIQUID-GLASS 2026-11-02, user] only the top controls are glass (the EOM | Dashboard switch, the month, Download PDF / Excel).
   "make the eom table just like it was before" + "in the dashboard side also, make it same just like it was before": the EOM
   tables and the whole Dashboard view keep their old look. The PDF / Excel files are built from the numbers (utils/eomReport.ts,
   utils/xlsx.ts), never from the screen. */
import { lgButtonSecondary, lgOnWhite, LG_PRESS, LgPressProps } from '../styles/liquidGlass';
import { fetchEOMSummary, EOMSummary, DeptRow, fetchEOMadjustments, saveEOMadjustment, fetchFilters } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { latestDataMonth } from '../utils/monthFilter';
import { addMonthsToYYYYMM, atOrAfterMinMonth } from '../utils/effectiveMonth';
import GlassSelect from '../components/GlassSelect';
import { toastSuccess, toastError } from '../components/toastService';
import { SkeletonBox, TableSkeleton, Reveal } from '../components/Skeleton';
import { LoadFailed } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { fmtDayRangeLabel } from '../utils/dates'; // [DATE-STYLE 2026-09-29] "01-10 Sep 2026" → "Sep 1–10, 2026"
import { downloadEomReport } from '../utils/eomReport'; // [PDF-EXPORT 2026-09-30]
import { downloadXlsx } from '../utils/xlsx'; // [EOM-XLSX 2026-10-08]
import { buildEomDashboardXlsx, EomXlsxSheet } from '../utils/eomDashboardXlsx'; // [EOM-XLSX 2026-10-08]
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-23] the Summary chart's phone width
import { layoutMax, layoutMin } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width


/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
const MONTH_SHORT_FMT = new Intl.DateTimeFormat('en-US', { month: 'short' });

/* ── Helpers ── */

// [EFFECTIVE-MONTH 2026-07-01] App-wide effective current month (12:30 PM IST
// rollover on the 1st) via latestDataMonth so the default month AND the non-admin
// previous-month edit lock roll over in lockstep with the rest of the app.
function getCurrentMonth(): string {
  return latestDataMonth();
}

function formatMonthTitle(m: string): string {
  const [y, mo] = m.split('-');
  const date = new Date(Number(y), Number(mo) - 1);
  return MONTH_LONG_YEAR_FMT.format(date);
}

function fmtCurrency(n: number): string {
  const prefix = n < 0 ? '$-' : '$';
  return `${prefix}${MONEY_FMT.format(Math.abs(n))}`;
}

function fmtPercent(n: number): string {
  return `${n.toFixed(1)}%`;
}

/* ── Layout ── */

const Page = styled.div`
  min-height: 100%;
  background: ${(p) => p.theme.surface};
  padding: 24px;
  /* Clear the floating bottom dock so the last rows (Net SEO Total / Difference)
     sit above the pill instead of hiding behind it. */
  padding-bottom: 96px;
`;

const TitleRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 38px;
  margin-bottom: 16px;

  /* [MOBILE 2026-07-07] Freeze each section's heading at the top on phones. EOM is
     a multi-section report that stacks into one column on mobile, so position:sticky
     here pins the current section's title (EOM | month, then Department Wise Revenue
     | month) to the top of the scrolling card while its rows
     scroll under it — and hands off to the next section's title as you pass it. The
     top heading no longer scrolls out of view. Opaque surface bg + hairline so rows
     never show through. (The month picker used to be absolutely positioned in here;
     it now has its own row above — see FilterRow.) Desktop is untouched. */
  @media (max-width: 768px) {
    /* [PHONE-LAYOUT 2026-10-23, user] NOT frozen any more (was position: sticky, top 0, z-index 6): on a phone nothing is
       fixed at the top — each heading scrolls away with its section ("in the phone view nothing will be fixed on the top
       side otherwise how will i see the data for the below"). */
    position: relative;
    margin-bottom: 12px;
    padding: 8px 0;
    background: ${(p) => p.theme.surface};
    border-bottom: 1px solid ${(p) => p.theme.line};
  }
`;

const MonthSelect = GlassSelect;

/* [DASHBOARD VIEW 2026-09-03, user] The at-a-glance version of the EOM month.

   ⛔ COLOUR: the reference mock was teal; this uses the APP's green (theme.greenPanel/greenPanelDeep,
   which is green600/700 in LIGHT and a deeper pair in dark — see the token — the same
   pair the toggle thumb and every other accent uses) so the tab does not introduce a second brand
   colour. Both themes are handled explicitly — the band keeps its green in light and dark, while
   the cards inside it sit on the theme surface so text contrast holds either way. Nothing here
   hard-codes a light-mode-only colour. */
/* [SKELETON 2026-09-03, user] $plain drops the green for a neutral placeholder fill while
   keeping the padding and radius identical, so the loading state occupies the same box without
   pretending the data is already there. The green arrives with the numbers. */
const DashBand = styled.div<{ $plain?: boolean }>`
  border-radius: 16px;
  padding: 16px;
  background: ${(p) => (p.$plain
    ? (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.05)')
    : `linear-gradient(160deg, ${p.theme.greenPanel}, ${p.theme.greenPanelDeep})`)};
  /* [DARK PANEL 2026-09-03, user] Tokenised: a GREEN glow under the card is the other
     half of what read as "too bright" on the dark ground. Light keeps the exact same value. */
  box-shadow: 0 10px 30px ${(p) => p.theme.greenPanelShadow};
`;


/* [DASHBOARD 2026-09-03, user] The report header inside the green band. The "(Overall)" /
   "(USA)" / "(ROW)" suffix is not decoration — the SAME card labelled "Overall TSA" means the
   whole company on one board and one slice of it on the next, so only the title tells them apart.
   White ink: it sits directly on the green gradient in BOTH themes, so it does not take a
   theme token. */
const DashHead = styled.h2`
  margin: 0 0 14px;
  text-align: center;
  /* [SIZE PARITY 2026-09-03, user] 15px, the same as the Summary table's heading, so the two
     halves read as one report instead of two documents at different scales. */
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.1px;
  color: #ffffff;

  span { color: rgba(255, 255, 255, 0.82); font-weight: 750; }
  @media (max-width: 640px) { font-size: 14px; }
`;

const DashKpis = styled.div`
  display: grid;
  /* 2x2 in the half-width column: auto-fit at 210px fitted THREE across and left the fourth card
     alone on its own row. Four across only once there is genuinely room for it. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  ${layoutMin(1900)} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const DashKpi = styled.div`
  border-radius: 12px;
  padding: 10px 12px 11px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(24,24,24,0.80)' : '#ffffff')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.06)')};

  .label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.4px;
    color: ${(p) => p.theme.text};
  }
  .value {
    margin-top: 6px;
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: ${(p) => p.theme.greenInk};
  }
  .delta { margin-top: 4px; font-size: 10px; font-weight: 700; }
  .delta.flat { color: ${(p) => p.theme.muted}; }

  /* [PHONE-LAYOUT 2026-10-23] phones: 11 px, the smallest label size on a phone (was 10) */
  @media (max-width: 768px) { .label, .delta { font-size: 11px; } }
`;

const DashSummary = styled.div`
  margin-top: 16px;
  border-radius: 16px;
  padding: 16px 18px 12px;
  /* [FILL THE CARD 2026-09-03, user] The card stretches to match the 22-row summary table beside
     it, so it is much taller than an aspect-locked chart. It used to CENTRE that chart, which
     parked a small graph in the middle of a tall box with dead space above and below — the user:
     "we are wasting too much data also in that section".
     Now the legend is pinned to the top and the chart takes every remaining pixel: flex-start
     here, flex: 1 on ChartScrollDash, and a viewBox whose aspect tracks the measured box so the
     svg fills rather than letterboxes. */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.line};
`;

const DashLegend = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  /* Sits at the very top of the card now (see DashSummary) and must not absorb any of the height
     the chart below it is claiming. */
  flex: 0 0 auto;
  /* [NUDGE 2026-09-03, user] The row sits a little lower off the card's top edge — but the two
     margins still SUM TO 10px, exactly what margin-bottom alone used to be. A plain margin-top
     grew the row, which grew the whole section ("it made the right side complete part a little
     big"); trading it against the bottom moves the line without costing a single pixel. */
  margin-top: 8px;
  margin-bottom: 2px;

  .title { font-size: 12.5px; font-weight: 800; color: ${(p) => p.theme.text}; margin-right: 6px; }
  span { display: inline-flex; align-items: center; gap: 7px; font-size: 11.5px; font-weight: 700; }
  i { width: 16px; height: 16px; border-radius: 4px; display: inline-block; }
`;

const ChartScrollDash = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* Takes every pixel the card has left under the legend. min-height: 0 is the load-bearing half:
     without it a flex child refuses to shrink below its content and the card grows instead of the
     chart fitting. align-items: stretch is what lets the svg height: 100% resolve to something real. */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: stretch;

  > svg { flex: 1 1 auto; }

  /* [PHONE-LAYOUT 2026-10-23] phones: a fixed 380 px tall box; the chart inside is drawn PHONE_CHART_W wide (DashChart)
     and swipes sideways, so its labels keep a readable size */
  @media (max-width: 768px) {
    flex: none;
    height: 380px;
    overscroll-behavior-x: contain;
  }
`;

const DashProv = styled.div<{ $plain?: boolean }>`
  margin-top: 16px;
  border-radius: 14px;
  padding: 11px 14px;
  text-align: center;
  font-size: 14px;
  font-weight: 800;
  color: ${(p) => (p.$plain ? 'transparent' : '#fff')};
  background: ${(p) => (p.$plain
    ? (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.05)')
    : `linear-gradient(160deg, ${p.theme.greenPanel}, ${p.theme.greenPanelDeep})`)};

  b { font-weight: 800; }
  @media (max-width: 640px) { font-size: 13px; }
`;
/* [VIEW SLIDE 2026-09-03, user] The two views slide past each other: pressing Dashboard sends the
   EOM report out to the LEFT while the dashboard comes in from the right.

   ⚠ overflow-x: CLIP, not hidden. `hidden` would make this a scroll container, and that silently
   breaks the `position: sticky` heading the EOM tables rely on at <=768px. `clip` hides the
   off-screen panel without creating one.

   Only the ACTIVE panel is in flow — the outgoing one is absolutely positioned, so the page takes
   the height of the view you are looking at instead of always the taller of the two. */
const Viewport = styled.div`
  position: relative;
  /* CLIP BOTH AXES. Clipping only the X axis left a tall stretch of empty page below the EOM
     view: the hidden panel is absolutely positioned inside here, and now that the Dashboard
     holds two boards plus two 22-row tables it is far taller than the EOM view — clipped
     sideways but still overflowing DOWNWARD, so it kept stretching the page scroll height
     with nothing visible in it. Clipping both axes bounds it to the visible panel.
     Still clip and NOT hidden: hidden would make this a scroll container and break the
     position:sticky headings the EOM tables use at <=768px.
     (No backticks in this comment on purpose — one ends the styled template literal.) */
  overflow: clip;
`;

const Panel = styled.div<{ $active: boolean; $from: 'left' | 'right' }>`
  position: ${(p) => (p.$active ? 'relative' : 'absolute')};
  top: 0;
  left: 0;
  right: 0;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  pointer-events: ${(p) => (p.$active ? 'auto' : 'none')};
  transform: translateX(${(p) => {
    if (p.$active) return '0';
    return p.$from === 'left' ? '-100%' : '100%';
  }});
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* Placeholder height so switching to the empty Dashboard does not collapse the page to nothing. */
/* [DASH SPLIT 2026-09-03, user] The two boards move into the LEFT half; the right half is
   reserved for a table to come. Collapses to one column at the same 1340px breakpoint the EOM
   tables use, so the two views break at the same width instead of at two different ones.
   align-items: start keeps the right column from stretching to the boards' full height. */
/* [SUMMARY TABLE 2026-09-03, user] This month against last, for one side of the business. Same
   rows and same order as the EOM table on the other view, so the two read as one report. */
const SumTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  /* [WEIGHT 2026-09-03, user] Matched to the page's own EOM table, which never looked slim:
     13px not 12px, weight 500 not the inherited 400, and roomier padding. All three together were
     what made this read as thin. */
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th, td { padding: 6px 10px; white-space: nowrap; }
  thead th {
    text-align: right;
    font-size: 10px;
    @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #ffffff;
    background: ${(p) => p.theme.greenPanelDeep};
  }
  thead th:first-child { text-align: left; }
  td { text-align: right; font-weight: 500; color: ${(p) => p.theme.text}; }
  td:first-child { text-align: left; font-weight: 500; }
  /* [WEIGHT 2026-09-03, user] Variance is the only column carrying colour, and at 12px in the
     default 400 weight the red and green read as thin and washed out. It is the column the eye is
     meant to land on, so it gets the weight to match. */
  td:last-child { font-weight: 750; }
  tbody tr:nth-child(even) { background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.025)' : 'rgba(22,25,23,0.018)')}; }

  /* Subtotal / total rows carry the weight, exactly as they do on the EOM table. */
  tbody tr.tot td {
    font-weight: 800;
    color: #ffffff;
    background: ${(p) => p.theme.greenPanel};
  }
`;

const SumScroll = styled.div`
  overflow-x: auto;
`;

const SumWrap = styled.div`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => p.theme.surface};
`;

const SumHead = styled.div`
  padding: 12px 14px;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(180deg, ${(p) => p.theme.greenPanel}, ${(p) => p.theme.greenPanelDeep});

  span { color: rgba(255, 255, 255, 0.82); }
`;

/* The left half of a row. A flex column so the chart card can take the leftover height and the
   board finishes level with the table beside it. min-width: 0 keeps the chart from forcing the
   grid column wider than its share. */
const DashCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  /* The board renders as a fragment, so its three children land here directly: the KPI band, the
     summary card, then the provisional strip. Only the middle one should stretch. */
  > div:nth-child(2) { flex: 1; }
`;

const DashSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  /* [ALIGN 2026-09-03, user] stretch, not start: the summary table is taller than the
     board, and with start the left half stopped short and left a ragged gap. The left
     column is a flex column whose chart card absorbs the slack (see DashSummary). */
  align-items: stretch;

  ${layoutMax(1340)} {
    grid-template-columns: 1fr;
  }
`;

/* Holds the space for the table that is coming, so the left half does not stretch to full width
   in the meantime. Deliberately empty — no border, no placeholder text. */
const DashAside = styled.div`
  min-width: 0;
`;

/* Breathing room between one board and the next (Overall / USA / ROW). */
const DashDivide = styled.div`
  height: 26px;
`;

const DashboardBlank = styled.div`
  min-height: 420px;
`;

/* [VIEW TOGGLE 2026-09-03, user] EOM / Dashboard, sitting to the LEFT of the month picker.
   Same segmented-control recipe as DialingReportPage (sliding thumb, pill, green fill) so it
   reads as the app's own control rather than a new invention. EOM is the default. */
const Segmented = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 3px;
  border-radius: 999px;
  /* [LIQUID-GLASS 2026-11-02, user] the track pressed into the glass; the thumb glossy, sliding the theme switch's way */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;
const SegThumb = styled.div<{ $right: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / 2);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')}, inset 0 1px 0 rgba(255,255,255,0.45);
  transform: translateX(${(p) => (p.$right ? '100%' : '0')});
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1); /* [LIQUID-GLASS] the theme switch's slide */
`;

const Seg = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  padding: 7px 18px;
  border: none;
  background: transparent;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover { color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)}; }
`;

type EomView = 'eom' | 'dashboard';

/** [FLUID-6 2026-10-12, user] "it feels laggy when I try to change it": the EOM / Dashboard pill. Its thumb used to move
 *  only after the WHOLE page had re-rendered for the new view (~0.07 s on a dev build, more on a busy PC), so it lagged
 *  the click. The pill keeps the view just picked itself, so the thumb moves at the click; the page's own switch then
 *  follows as a low-priority update (startTransition) that can never hold the thumb back. */
const EomViewToggle: React.FC<{ view: EomView; onPick: (v: EomView) => void }> = ({ view, onPick }) => (
  // [FLUID-8 2026-10-12, user] Now InstantPick (the same, shared): the thumb moves on the mouse PRESS.
  <InstantPick value={view} onPick={onPick}>
    {(shown, press) => (
      <Segmented role="tablist" aria-label="View">
        <SegThumb $right={shown === 'dashboard'} aria-hidden="true" />
        <Seg type="button" role="tab" aria-selected={shown === 'eom'} $active={shown === 'eom'} {...press('eom')}>EOM</Seg>
        <Seg type="button" role="tab" aria-selected={shown === 'dashboard'} $active={shown === 'dashboard'} {...press('dashboard')}>Dashboard</Seg>
      </Segmented>
    )}
  </InstantPick>
);

/* [FILTER ROW 2026-09-03, user] The month picker now sits on its OWN row, right-aligned, above
   both section headings — it used to be absolutely positioned inside the "Department Wise Revenue"
   heading, which is why that title carried a 160px right padding to avoid sliding under it. Giving
   the picker its own row drops that hack and lets both headings centre in their real width. The
   bottom margin is what shifts the two headings down. */
const FilterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;

  @media (max-width: 768px) {
    margin-bottom: 14px;
    /* [PDF-EXPORT 2026-09-30] Three controls do not fit one phone line: the Download PDF
       button (or the month picker as well) drops to a second right-aligned line. */
    flex-wrap: wrap;
    /* [PHONE-LAYOUT 2026-10-23] ...centred, as every phone page head: the EOM | Dashboard pill, then the month + Download
       line under it (both were pushed to the right edge). */
    justify-content: center;
  }
`;

/* [PDF-EXPORT 2026-09-30, user] "Download PDF" for the EOM view. The neutral glass recipe of
   DownloadButton in InventoryPage (32px tall, 10px corners, 13px/700), plus a disabled state
   while the month is loading or failed to load. */
/* [LIQUID-GLASS 2026-11-02, user] a glass capsule at the old size (32 px) with a squish and a glow where pressed; only the button
   changes — the PDF / Excel it makes are built from the numbers, untouched */
const PdfButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px;
  padding: 0 14px;
  gap: 6px;
  i { font-size: 11px; }
  &:disabled { opacity: 0.5; }
`;

/* [EOM PHONE SCROLL 2026-09-23, user] minmax(0, 1fr), not plain 1fr. A 1fr column never shrinks
   below its widest content, and the nowrap EOM table is wider than a phone. So the column
   stretched to the table's width, TableWrap had nothing to scroll, and the Viewport clip cut the
   right-hand columns off (the section title got cut too, centred in the too-wide column). With a
   0 minimum the column fits the screen and each TableWrap scrolls sideways as intended. The
   Dashboard view already does the same via min-width: 0 on DashCol / DashAside. Only the
   single-column (narrow) rule changed; the desktop 2-up rule is untouched. */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;

  /* [RESPONSIVE 2026-06-23] Was 1200px — but the 6-column EOM table needs ~615px
     and a 2-up column at 1280px is only ~587px, squeezing it. Collapse to one
     column a bit earlier so each table gets full width before it gets squeezed. */
  ${layoutMax(1340)} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0 0 16px 0;
  text-align: center;

  span {
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

/* ── Table styles ── */

const TableWrap = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 8px;
  /* [RESPONSIVE 2026-06-23] Was overflow:hidden, which silently clipped the
     rightmost columns of these nowrap tables on narrow/2-up layouts. Give each
     table its own horizontal scrollbar instead (rounded corners still clip). */
  overflow-x: auto;
  overflow-y: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Thead = styled.thead`
  /* [LIGHT-THEME-FIX 2026-06-23] Was a hardcoded dark-navy bar (#161917 + white
     text) in BOTH themes — fine in dark mode, but in light mode it read as an
     out-of-place black block among every other table's light header. Use the same
     themed treatment as DataTable / every other table: the theme "pill" surface
     with the green accent ink (light-grey + green600 in light, slate + mint in
     dark) and theme-aware hairline dividers. */
  background: ${(p) => p.theme.pill};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};

  th {
    padding: 10px 12px;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid ${(p) => p.theme.line};
    border-right: 1px solid ${(p) => p.theme.line};

    &:first-child {
      text-align: left;
    }
    &:last-child {
      border-right: none;
    }
  }
`;

const Tbody = styled.tbody`
  tr {
    border-bottom: 1px solid ${(p) => p.theme.line};

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 8px 12px;
    color: ${(p) => p.theme.text};
    text-align: right;
    white-space: nowrap;
    font-size: 13px;

    &:first-child {
      text-align: left;
      font-weight: 500;
    }
  }
`;

const TotalRow = styled.tr`
  background: ${(p) => p.theme.bg};
  font-weight: 700;

  td {
    font-weight: 700;
  }
`;

const SectionRow = styled.tr`
  background: ${(p) => p.theme.bg};
  td {
    font-weight: 700;
  }
`;

const InputCell = styled.td`
  && {
    padding: 4px 8px;
  }
`;

const AdjInput = styled.input`
  width: 88px;
  padding: 4px 8px;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 4px;
  background: transparent;
  color: ${(p) => p.theme.text};
  font-size: 12px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.green600};
  }

  /* Previous-month read-only (non-admins): visibly inert, not just blocked. */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

// [ADJ-$ 2026-07-01] Editable adjustment field — mirrors the Edit-Mapping money input:
// while focused you type just the amount and a live "$" prefix shows (e.g. "$400"); when
// not focused it displays the SAVED value as currency like every other EOM cell, WITH the
// ".00" (e.g. "$400.00"). Empty stays blank. Commits + saves on blur / Enter; Esc reverts.
const AdjField: React.FC<{ value: number; disabled?: boolean; onCommit: (v: number) => void }> = ({ value, disabled, onCommit }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  // Focused → live "$"+amount (edit-map style); blurred → formatted "$X.00" (EOM style), blank when 0.
  const shown = focused ? (draft === '' ? '' : `$${draft}`) : (value ? fmtCurrency(value) : '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Keep only digits + a single decimal point (drops the "$" and any stray chars).
    let raw = e.target.value.replace(/[^0-9.]/g, '');
    const dot = raw.indexOf('.');
    if (dot !== -1) raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '');
    setDraft(raw);
    // Pin the caret to the end so the live "$" prefix doesn't shove it left.
    requestAnimationFrame(() => { const el = ref.current; if (el) { const n = el.value.length; el.setSelectionRange(n, n); } });
  };

  const commit = () => {
    setFocused(false);
    const v = Number(draft) || 0;
    if (v !== value) onCommit(v);
  };

  return (
    <AdjInput
      ref={ref}
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={shown}
      placeholder=""
      onFocus={() => { setDraft(value ? String(value) : ''); setFocused(true); }}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') { setDraft(value ? String(value) : ''); e.currentTarget.blur(); }
      }}
    />
  );
};

const Dash = styled.span`
  color: ${(p) => p.theme.muted};
`;

/* ── Provisional Inventory Banner ── */

const ProvBanner = styled.div`
  position: relative;
  /* Liquid-glass green: top sheen + translucent diagonal tint + soft colored
     glow so the banner reads as a sheet of green glass instead of a flat fill. */
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 55%),
    linear-gradient(135deg, ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(14,126,78,0.88)' : 'rgba(26, 127, 100, 0.88)')}, ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.78)' : 'rgba(20, 107, 84, 0.78)')});
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch); the banner's own gradient carries the look. */
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.40),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12),
    0 10px 28px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(14,126,78,0.38)' : 'rgba(26, 127, 100, 0.38)')},
    0 1px 2px rgba(0, 0, 0, 0.10);
  color: #fff;
  border-radius: 12px;
  padding: 16px 24px;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
  margin: 20px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
`;

/* ── Retention Chart Area ── */

const RetentionSection = styled.div`
  margin-top: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RetentionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  text-align: center;
  margin: 0 0 16px 0;
`;

const ChartArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 12px;
  flex: 1;
  min-height: 180px;

  /* [PHONE-LAYOUT 2026-10-23] phones: the four periods did not fit a phone line and, centred, ran off BOTH edges ("9.4K",
     "ct 1-10" at the left, "$107." at the right). Here the chart swipes sideways in its own line, from the left edge; the
     top pad keeps the value label over the tallest bar inside the box. */
  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 14px;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    padding: 22px 2px 4px;
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 16px;
  font-size: 12px;
  color: ${(p) => p.theme.text};
`;

/* Premium bar fills: emerald-green for "Achieved", a clean white/silver glass
   for "Expected". The neutral fill adapts to the theme so the white bar stays
   visible against the light-mode card. */
type BarVariant = 'green' | 'neutral';

const barFill = (variant: BarVariant, dark: boolean) =>
  variant === 'green'
    ? (dark
      ? 'linear-gradient(180deg, #7de5ab 0%, #44b57b 52%, #3a9365 100%)' /* [GRAPHITE-EMERALD] */
      : 'linear-gradient(180deg, #65e7bf 0%, #13b790 52%, #1c9475 100%)')
    : dark
      ? 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 52%, #dfdfdf 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #eaedec 52%, #d1d5d3 100%)';

const barGlow = (variant: BarVariant) =>
  variant === 'green' ? 'rgba(16, 185, 129, ' : 'rgba(148, 163, 184, ';

const LegendDot = styled.span<{ $variant: BarVariant }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(p) => barFill(p.$variant, p.theme.colorScheme === 'dark')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  margin-right: 6px;
  vertical-align: middle;
`;

const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  max-width: 140px;

  /* [PHONE-LAYOUT 2026-10-23] phones: each period keeps its full width in the swiping chart (ChartArea) */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    max-width: none;
    min-width: 112px;
  }
`;

const BarContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 160px;
`;

const Bar = styled.div<{ $height: number; $variant: BarVariant }>`
  position: relative;
  width: 38px;
  height: ${(p) => p.$height}px;
  background: ${(p) => barFill(p.$variant, p.theme.colorScheme === 'dark')};
  border-radius: 7px 7px 0 0;
  min-height: 4px;
  /* Liquid-glass bar:
       • inset top highlight → glossy top edge
       • inset bottom darken → cylinder depth
       • outer colored glow → soft halo in the bar's own colour
       • subtle base shadow → grounds it on the chart floor */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 6px rgba(0, 0, 0, 0.16),
    0 6px 16px ${(p) => barGlow(p.$variant)}0.32),
    0 1px 2px rgba(0, 0, 0, 0.18);
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  /* Glassy top sheen — drawn as a separate gradient layer so it's clipped
     to the rounded top corners without affecting the absolute-positioned
     BarLabel which sits above the bar. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 55%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%);
    border-radius: 7px 7px 0 0;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.65),
      inset 0 -3px 6px rgba(0, 0, 0, 0.18),
      0 10px 22px ${(p) => barGlow(p.$variant)}0.45),
      0 1px 2px rgba(0, 0, 0, 0.22);
  }
`;

const BarLabel = styled.span`
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
  white-space: nowrap;

  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
`;

const BarPeriod = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.muted};
  text-align: center;
  white-space: nowrap;

  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
`;

/* ── Retention Table ── */

const RetentionThead = styled.thead`
  /* [LIGHT-THEME-FIX 2026-06-23] Same fix as <Thead>: themed pill header + green
     accent ink instead of a hardcoded dark-navy bar that looked out of place in
     light mode. */
  background: ${(p) => p.theme.pill};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};

  th {
    padding: 8px 10px;
    font-weight: 700;
    font-size: 10px;
    @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone */
    text-transform: uppercase;
    letter-spacing: 0.5px;
    /* [2026-07-07] Right-align the date-range headings so each sits over its
       right-aligned value column (was center). First child (Period) stays left. */
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid ${(p) => p.theme.line};
    border-right: 1px solid ${(p) => p.theme.line};

    &:first-child {
      text-align: left;
    }
    &:last-child {
      border-right: none;
    }
  }
`;

/* ── Component ── */

const ZERO: DeptRow = { ranking: 0, creative: 0, tech: 0, performance: 0 };

/**
 * [ONE SOURCE 2026-09-03] The whole EOM derivation, as a pure function of the server payload plus
 * the two adjustment rows. Lifted verbatim out of the component so the EOM table and the Dashboard
 * view (this month and last month) all read from one place — a figure cannot say one thing in the
 * table and another on a card.
 *
 * ⚠ Adjustments are part of the inputs, not an afterthought: Net New Sales and Net PM Total both
 * fold them in, and the Dashboard's "compared to last month" is only honest if last month's own
 * adjustments are applied to last month's data. Pass the matching pair.
 */
function deriveEomTotals(d: EOMSummary['eom'], salesAdj: DeptRow, pmAdj: DeptRow) {
  // [SHOPIFY 2026-10-19, user] The month's Shopify payouts (ranking slot) — inside Gross SEO Total, so Net SEO Total too.
  const sh = d.shopify ?? ZERO;
  const grossNewSales: DeptRow = {
    ranking: d.recurring_sales.ranking + d.one_time_sales.ranking,
    creative: d.recurring_sales.creative + d.one_time_sales.creative,
    tech: d.recurring_sales.tech + d.one_time_sales.tech,
    performance: d.recurring_sales.performance + d.one_time_sales.performance,
  };

  const netNewSales: DeptRow = {
    ranking: grossNewSales.ranking + d.sales_pia.ranking + d.sales_disputes_won.ranking + salesAdj.ranking - d.sales_refund_dispute.ranking,
    creative: grossNewSales.creative + d.sales_pia.creative + d.sales_disputes_won.creative + salesAdj.creative - d.sales_refund_dispute.creative,
    tech: grossNewSales.tech + d.sales_pia.tech + d.sales_disputes_won.tech + salesAdj.tech - d.sales_refund_dispute.tech,
    performance: grossNewSales.performance + d.sales_pia.performance + d.sales_disputes_won.performance + salesAdj.performance - d.sales_refund_dispute.performance,
  };

  const pmRetentionPct = {
    ranking: d.pm_inventory.ranking > 0 ? (d.pm_retention_achieved.ranking / d.pm_inventory.ranking) * 100 : 0,
    creative: d.pm_inventory.creative > 0 ? (d.pm_retention_achieved.creative / d.pm_inventory.creative) * 100 : 0,
    tech: d.pm_inventory.tech > 0 ? (d.pm_retention_achieved.tech / d.pm_inventory.tech) * 100 : 0,
    performance: d.pm_inventory.performance > 0 ? (d.pm_retention_achieved.performance / d.pm_inventory.performance) * 100 : 0,
  };

  const grossPmSo: DeptRow = {
    ranking: d.pm_recurring_upsell.ranking + d.pm_one_time_upsell.ranking,
    creative: d.pm_recurring_upsell.creative + d.pm_one_time_upsell.creative,
    tech: d.pm_recurring_upsell.tech + d.pm_one_time_upsell.tech,
    performance: d.pm_recurring_upsell.performance + d.pm_one_time_upsell.performance,
  };

  const netPmTotal: DeptRow = {
    ranking: d.pm_retention_achieved.ranking + grossPmSo.ranking + d.pm_pia.ranking + d.pm_disputes_won.ranking + pmAdj.ranking - d.pm_refunds_disputes.ranking,
    creative: d.pm_retention_achieved.creative + grossPmSo.creative + d.pm_pia.creative + d.pm_disputes_won.creative + pmAdj.creative - d.pm_refunds_disputes.creative,
    tech: d.pm_retention_achieved.tech + grossPmSo.tech + d.pm_pia.tech + d.pm_disputes_won.tech + pmAdj.tech - d.pm_refunds_disputes.tech,
    performance: d.pm_retention_achieved.performance + grossPmSo.performance + d.pm_pia.performance + d.pm_disputes_won.performance + pmAdj.performance - d.pm_refunds_disputes.performance,
  };

  const grossSeoTotal: DeptRow = {
    ranking: grossNewSales.ranking + d.sales_pia.ranking + d.pm_retention_achieved.ranking + grossPmSo.ranking + d.pm_pia.ranking + sh.ranking,
    creative: grossNewSales.creative + d.sales_pia.creative + d.pm_retention_achieved.creative + grossPmSo.creative + d.pm_pia.creative + sh.creative,
    tech: grossNewSales.tech + d.sales_pia.tech + d.pm_retention_achieved.tech + grossPmSo.tech + d.pm_pia.tech + sh.tech,
    performance: grossNewSales.performance + d.sales_pia.performance + d.pm_retention_achieved.performance + grossPmSo.performance + d.pm_pia.performance + sh.performance,
  };

  const seoRefundsAdj: DeptRow = {
    ranking: pmAdj.ranking + d.pm_disputes_won.ranking + salesAdj.ranking + d.sales_disputes_won.ranking - d.sales_refund_dispute.ranking - d.pm_refunds_disputes.ranking,
    creative: pmAdj.creative + d.pm_disputes_won.creative + salesAdj.creative + d.sales_disputes_won.creative - d.sales_refund_dispute.creative - d.pm_refunds_disputes.creative,
    tech: pmAdj.tech + d.pm_disputes_won.tech + salesAdj.tech + d.sales_disputes_won.tech - d.sales_refund_dispute.tech - d.pm_refunds_disputes.tech,
    performance: pmAdj.performance + d.pm_disputes_won.performance + salesAdj.performance + d.sales_disputes_won.performance - d.sales_refund_dispute.performance - d.pm_refunds_disputes.performance,
  };

  const netSeoTotal: DeptRow = {
    ranking: grossSeoTotal.ranking + seoRefundsAdj.ranking,
    creative: grossSeoTotal.creative + seoRefundsAdj.creative,
    tech: grossSeoTotal.tech + seoRefundsAdj.tech,
    performance: grossSeoTotal.performance + seoRefundsAdj.performance,
  };

  return {
    grossNewSales, netNewSales, pmRetentionPct, grossPmSo, netPmTotal,
    grossSeoTotal, seoRefundsAdj, netSeoTotal,
  };
}

/** One month reduced to the handful of figures the Dashboard view shows. */
export interface EomMonthFigures {
  /** Net SEO Total for whichever side this is: the whole month, USA only, or ROW only. */
  overallTsa: number;
  newSales: number;          // Recurring + One-Time New Sales (gross)
  pmTotal: number;           // Net PM Total
  pmUpsell: number;          // Recurring + One-Time PM upsell
  recurringNewSales: number;
  oneTimeNewSales: number;
  pmRecurringUpsell: number;
  pmOneTimeUpsell: number;
  pmInventory: number;
  pmRetentionAchieved: number;
  provInventory: number;
  /**
   * Every line the Summary table shows, for THIS side of the business — the same rows, in the same
   * order, as the EOM table on the other view. Kept as one object so the table renders straight
   * from it and cannot drift from the tiles above it, which read the same figures.
   *
   * ⚠ SIGN CONVENTION: money-out rows (refunds, disputes) are stored NEGATIVE, so the table adds
   * everything and never has to know which rows to subtract.
   */
  lines: {
    salesRecurring: number;
    salesOneTime: number;
    grossNewSales: number;
    salesPia: number;
    salesRefundDispute: number;   // negative
    salesDisputeWon: number;
    salesAdjustment: number;
    netNewSales: number;
    pmInventory: number;
    pmRetentionAchieved: number;
    pmRetentionPct: number | null;
    pmRecurringUpsell: number;
    pmOneTimeUpsell: number;
    grossPmSo: number;
    pmPia: number;
    pmRefundDispute: number;      // negative
    pmDisputeWon: number;
    pmAdjustment: number;
    netPmTotal: number;
    /** [SHOPIFY 2026-10-19] The month's Shopify payouts — never ROW, so Overall = USA and ROW is 0. */
    shopify: number;
    grossSeoTotal: number;
    seoRefundsAdj: number;
    netSeoTotal: number;
  };
}

/**
 * [ROW DASHBOARD 2026-09-03, user] One month's figures for ONE SIDE of the business — or, since
 * [OVERALL BOARD 2026-09-03, user], for the whole company.
 *
 * `side` is 'overall', 'usa' or 'row'.
 *   overall → the whole month, exactly what the EOM tab's own table reports
 *   row     → straight from the server's rowDetail
 *   usa     → the whole month MINUS that same rowDetail
 *
 * Deriving USA by subtraction rather than computing it separately is what guarantees the boards add
 * back to the month exactly — the ROW numbers are built from the same predicates and money columns
 * as the totals they come out of (see EomRowSplit.detail). Compute USA its own way and the boards
 * would quietly disagree.
 *
 * ⭐ THE INVARIANT THIS FUNCTION EXISTS TO HOLD: **Overall = USA + ROW, line for line.** It follows
 * from `pick` alone — (whole − row) + row === whole — and it is what makes the three boards
 * checkable against each other and against the EOM table. The ONE deliberate exception is
 * PM - Retention %, which is a RATIO: each side divides its own retention by its own inventory, so
 * it does not add up between boards and must never be derived by subtraction.
 *
 * ⚠ Overall TSA and PM Total are the other exception in HOW they are sourced: both are NET (after
 * refunds, disputes and the PM adjustment), so both are read off the Department Wise Revenue rows,
 * which already carry the ROW split as their own rows. Overall sums ALL of them, USA the non-ROW
 * ones, ROW the ROW ones — no subtraction needed, and each ties to the table by construction.
 */
function monthFigures(
  summary: EOMSummary | null, salesAdj: DeptRow, pmAdj: DeptRow, side: 'overall' | 'usa' | 'row',
): EomMonthFigures | null {
  if (!summary?.eom) return null;
  const d = summary.eom;
  const sum = (r: DeptRow) => r.ranking + r.creative + r.tech + r.performance;
  const isRow = side === 'row';
  const isOverall = side === 'overall';

  // The ROW share of each bucket. Absent (older payload / split failed) it is all zeroes, which
  // makes the USA board the whole month and the ROW board empty — the old un-split behaviour.
  // The Overall board is unaffected by that fallback: it never reads rd at all.
  const rd = summary.rowDetail || {
    salesRecurring: 0, salesOneTime: 0, salesPia: 0,
    pmRetention: 0, pmRecurringUpsell: 0, pmOneTimeUpsell: 0, pmPia: 0,
    pmInventory: 0, provInventory: 0,
    salesRefundDispute: 0, salesDisputeWon: 0, pmRefundDispute: 0, pmDisputeWon: 0,
  };
  /** Overall → the whole month; ROW → the ROW figure; USA → everything else. */
  const pick = (whole: number, row: number) => (isOverall ? whole : isRow ? row : whole - row);

  /* Overall TSA and PM Total are both NET, so both come off the Department Wise Revenue rows.
     Those rows already carry the ROW split for gross, refunds AND disputes won, so the netting is
     correct for each side on its own.
     ⚠ Do NOT go back to netting the whole month and subtracting only ROW's GROSS buckets. That
     gives the same answer today ONLY because ROW has had no refunds and no won disputes in any
     month of 2026 (all eight checked) — the first ROW refund would leave that money sitting in the
     USA figure, and the two boards would stop tying to the table. */
  const pmAdjTotal = sum(pmAdj);
  const rows = summary.deptRevenue || [];
  /* Overall takes EVERY dept row — that is the whole point of it, and it is why the four rows sum
     back to the month. USA/ROW keep the either/or split they always had. */
  const mine = isOverall ? rows : rows.filter((x) => isRow === /^ROW\b/i.test(x.team.trim()));
  const netOf = (x: { team: string; gross: number; refundDispute: number; disputeWon: number }) =>
    /* The PM adjustment belongs to the USA "PM" row, exactly as the table applies it. The test is
       an EXACT match on 'PM', never a suffix test — 'ROW - PM' must not pick it up, and on the
       Overall board (which holds both rows) a loose test would add the adjustment TWICE. */
    x.gross - x.refundDispute + (x.team.trim() === 'PM' ? x.disputeWon + pmAdjTotal : x.disputeWon);
  const overallTsa = mine.reduce((acc, x) => acc + netOf(x), 0);
  const pmTotalSide = mine.filter((x) => /PM$/i.test(x.team.trim())).reduce((acc, x) => acc + netOf(x), 0);

  const recurringNewSales = pick(sum(d.recurring_sales), rd.salesRecurring);
  const oneTimeNewSales = pick(sum(d.one_time_sales), rd.salesOneTime);
  const pmRecurringUpsell = pick(sum(d.pm_recurring_upsell), rd.pmRecurringUpsell);
  const pmOneTimeUpsell = pick(sum(d.pm_one_time_upsell), rd.pmOneTimeUpsell);

  /* The Summary table's rows, for this side.
     ⭐ ADJUSTMENTS ARE NOT-ROW, which is why the test is `isRow` and not `side === 'usa'`.
     `eom_adjustments` holds one manual figure per month per side of the business with NO ROW
     notion at all, so there is nothing to bifurcate: the whole of it belongs to USA, and therefore
     the whole of it also belongs to Overall. Both boards show it, ROW shows zero, and
     USA + ROW still equals Overall on the adjustment rows exactly as on every other row.
     ⛔ Do NOT "fix" this by moving the adjustment to Overall only (asked and decided 2026-09-03):
     the Department Wise Revenue rows already fold the PM adjustment into the USA "PM" row, so USA
     would stop tying to those rows and the clean Overall = USA + ROW sum would be lost. */
  const sAdj = isRow ? 0 : sum(salesAdj);
  const pAdj = isRow ? 0 : pmAdjTotal;
  const L_salesRecurring = recurringNewSales;
  const L_salesOneTime = oneTimeNewSales;
  const L_grossNewSales = L_salesRecurring + L_salesOneTime;
  const L_salesPia = pick(sum(d.sales_pia), rd.salesPia);
  const L_salesRefundDispute = -pick(sum(d.sales_refund_dispute), rd.salesRefundDispute);
  const L_salesDisputeWon = pick(sum(d.sales_disputes_won), rd.salesDisputeWon);
  const L_netNewSales = L_grossNewSales + L_salesPia + L_salesDisputeWon + sAdj + L_salesRefundDispute;
  const L_pmInventory = pick(sum(d.pm_inventory), rd.pmInventory);
  const L_pmRetention = pick(sum(d.pm_retention_achieved), rd.pmRetention);
  const L_pmRecurring = pmRecurringUpsell;
  const L_pmOneTime = pmOneTimeUpsell;
  const L_grossPmSo = L_pmRecurring + L_pmOneTime;
  const L_pmPia = pick(sum(d.pm_pia), rd.pmPia);
  const L_pmRefundDispute = -pick(sum(d.pm_refunds_disputes), rd.pmRefundDispute);
  const L_pmDisputeWon = pick(sum(d.pm_disputes_won), rd.pmDisputeWon);
  const L_netPmTotal = L_pmRetention + L_grossPmSo + L_pmPia + L_pmDisputeWon + pAdj + L_pmRefundDispute;
  // [SHOPIFY 2026-10-19] No ROW share (a Shopify payout has no PM, deal owner or ROW stamp): Overall and USA get it all.
  const L_shopify = pick(sum(d.shopify ?? ZERO), 0);
  const L_grossSeo = L_grossNewSales + L_salesPia + L_pmRetention + L_grossPmSo + L_pmPia + L_shopify;
  const L_seoAdj = pAdj + L_pmDisputeWon + sAdj + L_salesDisputeWon + L_salesRefundDispute + L_pmRefundDispute;

  return {
    overallTsa,
    newSales: recurringNewSales + oneTimeNewSales,
    // Net PM Total for this side, straight off the correctly-split dept rows.
    pmTotal: pmTotalSide,
    pmUpsell: pmRecurringUpsell + pmOneTimeUpsell,
    recurringNewSales,
    oneTimeNewSales,
    pmRecurringUpsell,
    pmOneTimeUpsell,
    pmInventory: pick(sum(d.pm_inventory), rd.pmInventory),
    pmRetentionAchieved: pick(sum(d.pm_retention_achieved), rd.pmRetention),
    provInventory: pick(summary.provInventory || 0, rd.provInventory),
    lines: {
      salesRecurring: L_salesRecurring,
      salesOneTime: L_salesOneTime,
      grossNewSales: L_grossNewSales,
      salesPia: L_salesPia,
      salesRefundDispute: L_salesRefundDispute,
      salesDisputeWon: L_salesDisputeWon,
      salesAdjustment: sAdj,
      netNewSales: L_netNewSales,
      pmInventory: L_pmInventory,
      pmRetentionAchieved: L_pmRetention,
      // Per side, from that side's OWN inventory and retention — a ratio, so it never adds up
      // between the two boards and must not be derived by subtraction.
      pmRetentionPct: L_pmInventory > 0 ? (100 * L_pmRetention) / L_pmInventory : null,
      pmRecurringUpsell: L_pmRecurring,
      pmOneTimeUpsell: L_pmOneTime,
      grossPmSo: L_grossPmSo,
      pmPia: L_pmPia,
      pmRefundDispute: L_pmRefundDispute,
      pmDisputeWon: L_pmDisputeWon,
      pmAdjustment: pAdj,
      netPmTotal: L_netPmTotal,
      shopify: L_shopify,
      grossSeoTotal: L_grossSeo,
      seoRefundsAdj: L_seoAdj,
      netSeoTotal: L_grossSeo + L_seoAdj,
    },
  };
}

/** Whole dollars for on-chart labels — the cents are what made the pairs collide. */
const fmtWhole = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * [DASHBOARD CHART 2026-09-03, user] Six figures, this month against last, as paired bars.
 *
 * ⚠⚠ [PER-PAIR SCALE 2026-09-03, user] EACH PAIR HAS ITS OWN Y SCALE. There is no shared axis and
 * heights are NOT comparable between groups — a $700 bar and a $220,349 bar both reach full height.
 *
 * This REVERSES the original decision, deliberately and at the user's explicit instruction after
 * seeing the shared scale in use: "consider all the 6 parts separately, no comparison using y axis".
 * The shared scale was defensible in theory (they are all dollars) and unusable in practice — with
 * a 315:1 spread, five of the six pairs were slivers on the axis and the card was mostly white
 * space. What the reader actually needs from this chart is THIS MONTH vs LAST MONTH within a
 * figure, and that comparison is exactly what per-pair scaling makes legible: every pair now uses
 * the full height, so the month-on-month ratio is readable in all six instead of two.
 *
 * ⛔ The cross-group comparison is genuinely GONE. It is not hidden — every bar still carries its
 * own printed value, and the Summary table beside it holds the same figures in one shared column
 * where they ARE comparable. Do not "restore" a shared max without asking: it was removed on
 * purpose and the reason is above.
 *
 * The chart also FILLS its card. The viewBox height is computed from the measured box so the
 * viewBox aspect matches the container's, which makes `meet` fill it exactly instead of
 * letterboxing — the card stretches to the 22-row summary table beside it and used to leave most
 * of that height empty. Bars are hand-rolled SVG like every other chart in the app.
 */
/** [PHONE-LAYOUT 2026-10-23] The Summary chart's drawn width on a phone (it swipes sideways): every label 11 px or more. */
const PHONE_CHART_W = 880;

const DashChart: React.FC<{ rows: { label: string; now: number; prev: number }[] }> = ({ rows }) => {
  const theme = useTheme() as any;
  const dark = theme.colorScheme === 'dark';
  const nowFill = theme.green600 as string;
  const prevFill = dark ? 'rgba(255,255,255,0.34)' : 'rgba(22,25,23,0.32)';
  const ink = theme.text as string;

  /* [FILL THE CARD 2026-09-03, user] Measure the box the chart is given and shape the viewBox to
     the same aspect. Setting the viewBox never changes layout, so this cannot feed back into the
     observer. Before the first measurement (and anywhere ResizeObserver is missing) it falls back
     to the original 1200x310, which is the old behaviour exactly. */
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || r.width <= 0 || r.height <= 0) return;
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      // Bail when nothing moved: a setState per observer tick would re-render on every scroll.
      setBox((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = 1200;
  /* [PHONE-LAYOUT 2026-10-23] Phones: squeezed to the phone's width (its 460 px floor) the 1200-unit drawing put its
     labels at about 6-7 px. There it is drawn PHONE_CHART_W wide (it swipes sideways in ChartScrollDash, 380 px tall),
     so a value label (17 units) is about 12.5 px and a category (15 units) about 11 px; the viewBox's height follows that
     width, not the box's visible width. The laptop never takes this branch. */
  const isMobile = useIsMobile();
  const drawnW = isMobile && box ? Math.max(box.w, PHONE_CHART_W) : box?.w ?? 0;
  /* Clamped: a very short card must still leave room for the label above a full-height bar plus
     two lines of category label below (mt + mb = 78), and a very tall one should not produce
     comically stretched bars. */
  const H = box && box.w > 0
    ? Math.max(300, Math.min(760, Math.round((W * box.h) / drawnW)))
    : 310;
  const ml = 8, mr = 8, mt = 34, mb = 44;
  const ph = H - mt - mb;
  const band = (W - ml - mr) / Math.max(rows.length, 1);
  /* Wider than the old 0.28/54 now that each pair fills the height — thin bars at full height read
     as spikes. Still leaves ~59 units between groups, and >= LABEL_DX so each value label stays
     over its own bar (see LABEL_DX below). */
  const bw = Math.min(band * 0.32, 62);

  /** Each pair scales to ITS OWN larger value — the whole point of this chart. */
  const pairMax = rows.map((r) => Math.max(r.now, r.prev, 1));
  /* [SMALL BARS 2026-09-03, user] Within a pair the smaller value can still be a rounding error of
     the larger (PM SO/Upsell One Time: $1,030 against $78,980 = 1.3%), so a non-zero value always
     draws at least a visible sliver rather than nothing. Proportional to the plot height so it
     stays a sliver as the card grows. A genuine $0 still draws NOTHING — an empty slot is the
     honest signal there, and it is the one case the reader must not mistake for "very small". */
  const MIN_BAR = Math.max(3, ph * 0.012);
  /* [HEADROOM 2026-09-03, user] "make the size of this bar a little short from the top" — the
     tallest bar in each pair fills this much of the plot instead of all of it, so the row of
     full-height bars stops just short of the ceiling and its value labels get air above them.
     ⭐ THE BASELINE AND EVERY RATIO ARE UNTOUCHED: this scales all six pairs by the same factor,
     so within a pair the month-on-month proportion still reads exactly true. It is purely the
     top of the chart breathing. This single number is the knob — raise it toward 1 for taller
     bars, lower it for more air. */
  const BAR_FULL = 0.6;
  const barH = (v: number, i: number) =>
    (v > 0 ? Math.max(MIN_BAR, (v / pairMax[i]) * ph * BAR_FULL) : 0);
  const top = (v: number, i: number) => mt + ph - barH(v, i);

  /* [LABEL SIZE 2026-09-03, user] The chart is authored 1200 units wide but renders into the left
     half of DashSplit — ~778px at a 1920 viewport, ~538px at 1440 — so every unit is scaled by
     0.45–0.65 and an 11.5-unit label landed at 5–7.5 real pixels, smaller than any other text on
     the page. (Below 1340px the grid collapses to one column and the same labels JUMP to ~10px,
     which is why it read fine on a narrow window and cramped on a wide one.)
     ⚠ Shrinking the viewBox does nothing: scale every unit together and every rendered size,
     text included, is unchanged. The only lever is the font's size RELATIVE to the geometry, and
     the constraint on that is the two labels of a pair sharing one line. Centred over their bars
     they were 64 units apart, and "$220,349" is ~4.2 units per font-unit wide, so ~15 was the
     ceiling. LABEL_DX pushes each label 8 units OUTWARD from its bar centre — 80 apart — which
     buys a 17-unit font with room to spare, and a label centred 40 units off cx is still inside a
     bar that spans 5..59 off cx, so it still reads as sitting over its own bar. Next-group
     clearance is ~43 units. Cents stay dropped (the [LABEL COLLISION] fix that came first):
     "$220,348.82" would be ~90 wide and overlap on every tall bar. A vertical stagger was tried
     before that and rejected — on a short bar the lower label landed on the bar itself. */
  const LABEL_DX = 40;
  const VALUE_FONT = 17;
  const CAT_FONT = 15;
  const CAT_LINE = 16;

  return (
    /* The wrapper is what gets measured, and `height: 100%` on the svg is what makes the chart
       actually take the height the card gives it. */
    <ChartScrollDash ref={wrapRef}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? PHONE_CHART_W : 460 /* [PHONE-LAYOUT 2026-10-23] */ }}
        role="img" aria-label="This month against last month for each figure. Each pair is scaled to its own values; heights are not comparable between pairs.">
        <line x1={ml} y1={mt + ph} x2={W - mr} y2={mt + ph} stroke={theme.line} strokeWidth={1} />
        {rows.map((r, i) => {
          const cx = ml + band * i + band / 2;
          // 5 units either side of centre, not 3: it buys the value labels above the bars
          // another 4 units of clearance, which is what lets both sit on the same line.
          const xNow = cx - bw - 5;
          const xPrev = cx + 5;
          return (
            <g key={r.label}>
              <rect x={xNow} y={top(r.now, i)} width={bw} height={barH(r.now, i)} rx={5} fill={nowFill} />
              <rect x={xPrev} y={top(r.prev, i)} width={bw} height={barH(r.prev, i)} rx={5} fill={prevFill} />
              <text x={cx - LABEL_DX} y={top(r.now, i) - 10} textAnchor="middle" fontSize={VALUE_FONT} fontWeight={750} fill={theme.greenInk}>
                {fmtWhole(r.now)}
              </text>
              <text x={cx + LABEL_DX} y={top(r.prev, i) - 10} textAnchor="middle" fontSize={VALUE_FONT} fontWeight={700} fill={ink}>
                {fmtWhole(r.prev)}
              </text>
              {/* Two lines so a long label never overlaps its neighbour. */}
              {r.label.split('|').map((line, k) => (
                <text key={k} x={cx} y={mt + ph + 18 + k * CAT_LINE} textAnchor="middle" fontSize={CAT_FONT} fontWeight={700} fill={ink}>
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </ChartScrollDash>
  );
};

/* [EOM-XLSX 2026-10-08, user] The Summary table's rows and per-row arithmetic, the board's four
   tiles and the chart's six pairs, defined ONCE. Two things draw them — the Dashboard on screen and
   its "Download Excel" file — and both read these, so the file cannot disagree with the screen.
   Moved here unchanged from SummaryTable / DashBoard. */
type SummaryLines = EomMonthFigures['lines'];
type SummaryRowDef = { label: string; key: keyof SummaryLines; tot?: boolean; pct?: boolean };
const SUMMARY_ROWS: SummaryRowDef[] = [
  { label: 'SEO Recurring - New Sales', key: 'salesRecurring' },
  { label: 'SEO One Time - New Sales', key: 'salesOneTime' },
  { label: 'Gross New Sales', key: 'grossNewSales', tot: true },
  { label: 'Sales - PIA', key: 'salesPia' },
  { label: 'Sales - Refund/Disputes', key: 'salesRefundDispute' },
  { label: 'Sales - Disputes Won', key: 'salesDisputeWon' },
  { label: 'Sales - Adjustment', key: 'salesAdjustment' },
  { label: 'Net New Sales', key: 'netNewSales', tot: true },
  { label: 'PM - Inventory', key: 'pmInventory' },
  { label: 'PM - Retention Achieved', key: 'pmRetentionAchieved' },
  { label: 'PM - Retention %', key: 'pmRetentionPct', pct: true },
  { label: 'PM - SO/Upsell (Recurring)', key: 'pmRecurringUpsell' },
  { label: 'PM - SO/Upsell (One Time)', key: 'pmOneTimeUpsell' },
  { label: 'Gross PM SO Total', key: 'grossPmSo', tot: true },
  { label: 'PM - PIA', key: 'pmPia' },
  { label: 'PM - Refunds', key: 'pmRefundDispute' },
  { label: 'PM - Disputes Won', key: 'pmDisputeWon' },
  { label: 'PM - Adjustment', key: 'pmAdjustment' },
  { label: 'Net PM Total', key: 'netPmTotal', tot: true },
  { label: 'Shopify', key: 'shopify' }, // [SHOPIFY 2026-10-19]
  { label: 'Gross SEO Total', key: 'grossSeoTotal' },
  { label: 'SEO Refunds/Disputes/Adj', key: 'seoRefundsAdj' },
  { label: 'Net SEO Total', key: 'netSeoTotal', tot: true },
];

/** One Summary row as the table prints it: both months, Difference and Variance. */
function summaryRowFigures(r: SummaryRowDef, L: SummaryLines, P: SummaryLines | null) {
  const cur = L[r.key] as number | null;
  const was = P ? (P[r.key] as number | null) : null;
  const diff = cur != null && was != null ? cur - was : null;
  /* [EOM-VARIANCE 2026-10-08, user] "Current minus previous, divided by last month", on the
     amounts the table SHOWS, every row incl. Retention %. Refund rows are stored negative but
     shown as plain amounts, so the stored arithmetic printed -63% for refunds that ROSE 63%.
     Minus sign only when it fell, no "+" when it rose. Last month 0 (user, same day: "add it
     there too"): 100% when it grew from nothing, 0% when both are 0; blank only with no last month. */
  const shownNow = cur == null ? null : Math.abs(cur);
  const shownWas = was == null ? null : Math.abs(was);
  const varc = shownNow == null || shownWas == null
    ? null
    : shownWas !== 0
      ? ((shownNow - shownWas) / shownWas) * 100
      : (shownNow === 0 ? 0 : 100);
  // Rounded exactly as printed: whole per cent, two decimals on the Retention % row.
  const shownVar = varc == null ? null : r.pct ? Math.round(varc * 100) / 100 : Math.round(varc);
  return { cur, was, diff, shownVar };
}

/** The board's four tiles; pct = "% compared to last month" (null = no figure for last month). */
function boardKpis(now: EomMonthFigures, prev: EomMonthFigures | null): { label: string; cur: number; pct: number | null }[] {
  return ([
    ['Overall TSA', now.overallTsa, prev?.overallTsa],
    ['New Sales', now.newSales, prev?.newSales],
    ['PM Total', now.pmTotal, prev?.pmTotal],
    ['PM Upsell', now.pmUpsell, prev?.pmUpsell],
  ] as [string, number, number | undefined][]).map(([label, cur, was]) => ({
    label, cur, pct: was != null && was !== 0 ? ((cur - was) / Math.abs(was)) * 100 : null,
  }));
}

/** A tile's comparison line, exactly as the tile prints it. */
const kpiDeltaText = (pct: number | null) =>
  (pct == null ? 'no figure for last month' : `${pct >= 0 ? '+' : ''}${Math.round(pct)}% compared to last month`);

/** The chart's six pairs, this month against last ('|' = the label's line break). */
const chartPairs = (now: EomMonthFigures, prev: EomMonthFigures | null) => [
  { label: 'Recurring|New Sales', now: now.recurringNewSales, prev: prev?.recurringNewSales ?? 0 },
  { label: 'One Time|New Sales', now: now.oneTimeNewSales, prev: prev?.oneTimeNewSales ?? 0 },
  { label: 'PM SO/Upsell|(Recurring)', now: now.pmRecurringUpsell, prev: prev?.pmRecurringUpsell ?? 0 },
  { label: 'PM SO/Upsell|(One Time)', now: now.pmOneTimeUpsell, prev: prev?.pmOneTimeUpsell ?? 0 },
  { label: 'PM Inventory', now: now.pmInventory, prev: prev?.pmInventory ?? 0 },
  { label: 'PM Retention|Achieved', now: now.pmRetentionAchieved, prev: prev?.pmRetentionAchieved ?? 0 },
];

/** [EOM-XLSX 2026-10-08, user] One Dashboard row as an Excel tab — every value from the helpers above. */
function dashboardSheet(name: string, now: EomMonthFigures, prev: EomMonthFigures | null, month: string): EomXlsxSheet {
  return {
    name,
    boardTitle: `EOM Report (${name})`,
    summaryTitle: `Summary (${name})`,
    kpis: boardKpis(now, prev).map((k): EomXlsxSheet['kpis'][number] => ({
      label: k.label, value: k.cur, delta: kpiDeltaText(k.pct), tone: k.pct == null ? 'flat' : k.pct >= 0 ? 'up' : 'down',
    })),
    chart: chartPairs(now, prev),
    provisional: `Provisional Inventory for ${formatMonthTitle(addMonthsToYYYYMM(month, 1))}`,
    provisionalValue: fmtCurrency(now.provInventory),
    hasPrev: prev != null,
    table: SUMMARY_ROWS.map((r) => ({ label: r.label, tot: !!r.tot, pct: !!r.pct, ...summaryRowFigures(r, now.lines, prev?.lines ?? null) })),
  };
}

/**
 * [ROW DASHBOARD 2026-09-03, user] One dashboard board. Rendered twice — once for USA, once for
 * ROW — from the same component, so the two are identical in layout by construction and a change
 * to one is a change to both. Only the title and the figures differ.
 */
/**
 * [SUMMARY TABLE 2026-09-03, user] The month-against-month summary for ONE side.
 *
 * Difference is (current - last). Variance is (current - last) ÷ last month on the amounts the
 * table SHOWS, on every row incl. Retention % ([EOM-VARIANCE 2026-10-08, user]). When last month
 * was zero it shows 100% (grew from nothing) or 0% (zero both months) — never ∞, never blank.
 *
 * Colour follows the SIGN of the Variance (user, 2026-10-08): positive green, negative red — on
 * every row, refunds included; the green-bar subtotal rows stay white.
 */
const SummaryTable: React.FC<{
  title: string;
  now: EomMonthFigures;
  prev: EomMonthFigures | null;
  month: string;
  theme: any;
}> = ({ title, now, prev, month, theme }) => {
  const L = now.lines;
  const P = prev?.lines ?? null;
  const bad = theme.colorScheme === 'dark' ? '#f87171' : '#c0392b';
  // [EOM-XLSX 2026-10-08] The rows and their arithmetic now live in SUMMARY_ROWS / summaryRowFigures.

  /* [NEGATIVES 2026-09-03, user] A negative is shown as the plain amount in RED — no accounting
     brackets and no minus sign, by decision: the colour is the signal.
     ⚠ Consequence to be aware of: with neither bracket nor sign, a negative is indistinguishable
     from a positive in greyscale, in a print-out, or to a colour-blind reader. The row labels
     (Refunds, Disputes) still say which way the money went.
     `n + 0` normalises -0, which otherwise printed as "$-0" on a zero refund row. `onGreen`
     suppresses the colour on subtotal rows — those are white-on-green and red there is unreadable. */
  const money = (n: number, onGreen = false) => {
    const v = n + 0;
    const txt = fmtWhole(Math.abs(v));
    return v < 0 && !onGreen ? <span style={{ color: bad }}>{txt}</span> : txt;
  };

  return (
    <SumWrap>
      <SumHead>{title} <span>| The Scale Agency</span></SumHead>
      <SumScroll>
        <SumTable>
          <thead>
            <tr>
              <th>Particulars</th>
              <th>Current Month ({formatMonthTitle(month)})</th>
              <th>Last Month ({formatMonthTitle(addMonthsToYYYYMM(month, -1))})</th>
              <th>Difference</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {SUMMARY_ROWS.map((r) => {
              // [EOM-XLSX 2026-10-08] Shared with the Excel download — see summaryRowFigures.
              const { cur, was, diff, shownVar } = summaryRowFigures(r, L, P);
              /* Colour lives on the VARIANCE column alone. Tinting Difference as well meant most
                 rows carried two coloured cells and the eye had nowhere to land — the table read
                 as an alarm rather than a report.
                 [EOM-VARIANCE 2026-10-08, user] Colour follows the SIGN of the Variance as SHOWN:
                 positive green, negative red, 0% plain — refunds that rose are green too, by decision.
                 The green-bar (subtotal) rows stay white: `r.tot` below drops the tone. */
              const tone = shownVar == null || shownVar === 0
                ? undefined
                : { color: shownVar > 0 ? theme.greenInk : bad };
              return (
                <tr key={r.label} className={r.tot ? 'tot' : undefined}>
                  <td>{r.label}</td>
                  <td>{r.pct ? (cur == null ? '—' : `${cur.toFixed(2)}%`) : money(cur ?? 0, r.tot)}</td>
                  <td>{P == null ? '—' : r.pct ? (was == null ? '—' : `${was.toFixed(2)}%`) : money(was ?? 0, r.tot)}</td>
                  <td>
                    {diff == null ? '—' : r.pct ? `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%` : money(diff, r.tot)}
                  </td>
                  <td style={r.tot ? undefined : tone}>
                    {shownVar == null ? '' : r.pct ? `${(shownVar + 0).toFixed(2)}%` : `${shownVar + 0}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </SumTable>
      </SumScroll>
    </SumWrap>
  );
};

/**
 * [SKELETON ALIGNMENT 2026-09-03, user] The Dashboard's loading state.
 *
 * The old one was written when the Dashboard was a single full-width board: one band, one chart,
 * one strip. The view is now TWO rows, each a board on the left and a 22-row summary table on the
 * right — so the skeleton occupied nothing like the space the data does and everything jumped when
 * it landed.
 *
 * ⚠ Built from the REAL layout components (DashSplit / DashCol / DashAside / DashBand / DashKpis /
 * DashSummary / SumWrap), not lookalikes. That is the whole point: if the grid, the card padding
 * or the KPI columns change, the skeleton changes with them and cannot drift again.
 */
const DashPairSkeleton: React.FC = () => (
  <DashSplit>
    <DashCol>
      <DashBand $plain>
        <SkeletonBox $w="46%" $h="15px" $radius="6px" style={{ margin: '0 auto 14px' }} />
        <DashKpis>
          {[0, 1, 2, 3].map((i) => (
            <DashKpi key={i}>
              <SkeletonBox $w="58%" $h="10px" />
              <SkeletonBox $w="74%" $h="19px" $mt="8px" />
              <SkeletonBox $w="88%" $h="10px" $mt="6px" />
            </DashKpi>
          ))}
        </DashKpis>
      </DashBand>
      <DashSummary>
        <SkeletonBox $w="52%" $h="13px" />
        {/* [FILL THE CARD 2026-09-03] The real chart now takes the whole remaining height, so the
            placeholder has to as well — a fixed 190px block would leave the skeleton short and
            everything would jump when the data landed, which is the bug this skeleton exists to
            prevent. minHeight keeps it sane before flex resolves. */}
        <SkeletonBox $radius="12px" $mt="14px" style={{ flex: '1 1 auto', minHeight: 190 }} />
      </DashSummary>
      <DashProv $plain style={{ marginTop: 16 }}>
        <SkeletonBox $w="58%" $h="14px" style={{ margin: '0 auto' }} />
      </DashProv>
    </DashCol>
    <DashAside>
      <SumWrap>
        <SkeletonBox $h="42px" $radius="0" />
        {/* 23 rows ([SHOPIFY 2026-10-19] + Shopify), the same count the real table renders, so the two are the same height. */}
        <div style={{ padding: '0 10px' }}>
          {Array.from({ length: 23 }).map((_, i) => (
            <SkeletonBox key={i} $h="13px" $mt={i === 0 ? '10px' : '12px'} />
          ))}
          <div style={{ height: 10 }} />
        </div>
      </SumWrap>
    </DashAside>
  </DashSplit>
);

const DashBoard: React.FC<{
  title: string;
  now: EomMonthFigures;
  prev: EomMonthFigures | null;
  month: string;
  theme: any;
}> = ({ title, now, prev, month, theme }) => (
  <>
    <DashBand>
      <DashHead>{title} <span>- The Scale Agency</span></DashHead>
      <DashKpis>
        {/* [EOM-XLSX 2026-10-08] Tiles + their lines come from boardKpis / kpiDeltaText, shared with the Excel. */}
        {boardKpis(now, prev).map(({ label, cur, pct }) => (
          <DashKpi key={label}>
            <div className="label">{label}</div>
            <div className="value">{fmtCurrency(cur)}</div>
            <div className={`delta${pct == null ? ' flat' : ''}`}
              style={pct == null ? undefined : { color: pct >= 0 ? theme.greenInk : (theme.colorScheme === 'dark' ? '#f87171' : '#c0392b') }}>
              {kpiDeltaText(pct)}
            </div>
          </DashKpi>
        ))}
      </DashKpis>
    </DashBand>

    <DashSummary>
      <DashLegend>
        <span className="title">Summary</span>
        <span><i style={{ background: theme.green600 }} /> Current Month ({formatMonthTitle(month)})</span>
        <span><i style={{ background: theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.34)' : 'rgba(22,25,23,0.32)' }} />
          Last Month ({formatMonthTitle(addMonthsToYYYYMM(month, -1))})</span>
      </DashLegend>
      <DashChart rows={chartPairs(now, prev)} />
    </DashSummary>

    <DashProv>
      Provisional Inventory for {formatMonthTitle(addMonthsToYYYYMM(month, 1))} &nbsp;<b>{fmtCurrency(now.provInventory)}</b>
    </DashProv>
  </>
);

const EOMPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const theme = useTheme() as any;
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState<EOMSummary | null>(null);
  const [loading, setLoading] = useState(true);
  // [LOAD-STATES 2026-09-29, user] A failed load used to draw both tables full of $0.00. Now each
  // view shows its own "Couldn't load …" countdown card; bumping the reload counter re-runs its load.
  const [loadFailed, setLoadFailed] = useState(false);
  const [eomReload, setEomReload] = useState(0);
  const [dashFailed, setDashFailed] = useState(false);
  const [dashReload, setDashReload] = useState(0);
  const [salesAdj, setSalesAdj] = useState<DeptRow>({ ...ZERO });
  const [pmAdj, setPmAdj] = useState<DeptRow>({ ...ZERO });
  /*
   * [SEPARATE MONTHS 2026-09-03, user] The two views keep their OWN month and their OWN loading
   * flag. Changing the picker on one side must not blank the other: before this, one shared
   * `month` + one shared `loading` meant selecting a month on the Dashboard threw the EOM report
   * into its skeleton (and vice versa), because the skeleton wrapped both panels.
   *
   * The Dashboard's data is fetched LAZILY — only once the user has actually opened that view —
   * so an EOM-only visit still costs the same two requests it always did.
   */
  const [dashMonth, setDashMonth] = useState(getCurrentMonth());
  /** Which month dashData actually holds — lets a re-selection paint without a skeleton. */
  const dashDataMonthRef = useRef<string | null>(null);
  const [dashTouched, setDashTouched] = useState(false);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashData, setDashData] = useState<EOMSummary | null>(null);
  const [dashSalesAdj, setDashSalesAdj] = useState<DeptRow>({ ...ZERO });
  const [dashPmAdj, setDashPmAdj] = useState<DeptRow>({ ...ZERO });
  // Last month, for the Dashboard's "compared to last month" and its paired bars. Its OWN
  // adjustments too — comparing an adjusted month with an unadjusted one would misstate the change.
  const [prevData, setPrevData] = useState<EOMSummary | null>(null);
  const [prevSalesAdj, setPrevSalesAdj] = useState<DeptRow>({ ...ZERO });
  const [prevPmAdj, setPrevPmAdj] = useState<DeptRow>({ ...ZERO });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // [ACCESS 2026-07-20] PM + Sales adjustments are ADMIN-ONLY to edit. Non-admins
  // see the adjustment inputs read-only for every month (the backend PUT
  // /eom/adjustments enforces the same admin-only rule).
  const editLocked = !isAdmin;

  useEffect(() => {
    // Guard against out-of-order responses: when `month` changes we fire a new
    // request, but the previous one may still be in flight. Without this flag a
    // slow earlier response (e.g. the initial current-month load) can resolve
    // last and clobber the data for the month the user just selected.
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchEOMSummary(month),
      fetchEOMadjustments(month),
    ])
      .then(([summary, adj]) => {
        if (cancelled) return;
        setData(summary);
        setSalesAdj(adj.sales);
        setPmAdj(adj.pm);
        setLoadFailed(false); // [LOAD-STATES 2026-09-29]
      })
      // [LOAD-STATES 2026-09-29] Drop the previous month's figures: they are not this month's.
      .catch((err) => { if (!cancelled) { console.error(err); setData(null); setLoadFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, eomReload]);

  /* The Dashboard view's own load, keyed on its own month. Runs only after the view has been
     opened once, and sets only its own loading flag, so the EOM panel never flickers. */
  useEffect(() => {
    if (!dashTouched) return;
    let cancelled = false;
    const prev = addMonthsToYYYYMM(dashMonth, -1);

    /* [DASH SPEED 2026-09-03, user] Two shortcuts so switching month feels instant.
       1. When the dashboard is on the SAME month the EOM side already has loaded, reuse it rather
          than refetching the identical payload — that is the common case, since both default to
          the current month and the toggle does not change the month.
       2. Only show the skeleton when there is nothing to show. Re-selecting a month we already
          have paints immediately; otherwise the board would flash a skeleton over good data. */
    const canReuseCurrent = data != null && month === dashMonth;
    setDashLoading(dashData == null || dashDataMonthRef.current !== dashMonth);
    Promise.all([
      canReuseCurrent ? Promise.resolve(data) : fetchEOMSummary(dashMonth),
      canReuseCurrent ? Promise.resolve({ sales: salesAdj, pm: pmAdj }) : fetchEOMadjustments(dashMonth).catch(() => null),
      // Last month is a nicety: a failure must leave the board readable, not blank it.
      fetchEOMSummary(prev).catch(() => null),
      fetchEOMadjustments(prev).catch(() => null),
    ])
      .then(([summary, adj, prevSummary, prevAdj]) => {
        if (cancelled) return;
        dashDataMonthRef.current = dashMonth;
        setDashData(summary);
        setDashSalesAdj(adj ? adj.sales : { ...ZERO });
        setDashPmAdj(adj ? adj.pm : { ...ZERO });
        setPrevData(prevSummary);
        setPrevSalesAdj(prevAdj ? prevAdj.sales : { ...ZERO });
        setPrevPmAdj(prevAdj ? prevAdj.pm : { ...ZERO });
        setDashFailed(false); // [LOAD-STATES 2026-09-29]
      })
      .catch((err) => { if (!cancelled) { console.error(err); setDashFailed(true); } })
      .finally(() => { if (!cancelled) setDashLoading(false); });
    return () => { cancelled = true; };
    // NOTE: `data`/`salesAdj`/`pmAdj` are read only for the same-month shortcut above and are
    // deliberately NOT deps — including them would refetch the dashboard every time the EOM
    // table's own adjustment inputs change.
  }, [dashMonth, dashTouched, dashReload]);

  // [REALTIME] 2026-06-23 — silently refetch the EOM summary for the selected
  // month when the server signals a change. Adjustments are deliberately NOT
  // refetched here — they are user-owned local edits and must not be clobbered.
  useRealtime(['eom'], () => {
    fetchEOMSummary(month).then((d) => { setData(d); setLoadFailed(false); }).catch(() => {}); // [LOAD-STATES 2026-09-29]
  });

  // Default the month filter to the latest month that has data (EOM is driven
  // by payments, so we use the payments months list): the current month once
  // its data lands, otherwise the most recent prior month. Polls so it
  // auto-advances to the current month the moment that data appears, without
  // clobbering an older month the user has deliberately navigated to.
  const latestSeenMonthRef = useRef<string | null>(null);
  /** Set once the user chooses a month on the Dashboard side; stops the poll overriding it. */
  const dashMonthTouchedRef = useRef(false);
  useEffect(() => {
    let active = true;
    const apply = (months: string[]) => {
      if (!active) return;
      const target = latestDataMonth(months);
      if (latestSeenMonthRef.current === null || target > latestSeenMonthRef.current) {
        latestSeenMonthRef.current = target;
        setMonth(target);
        /* [SEPARATE MONTHS 2026-09-03] The Dashboard follows this default too, but ONLY until the
           user picks a month there themselves — otherwise its picker would be silently dragged
           back by the next poll. Without this it stayed on the raw current month while the EOM
           side fell back to the latest month that actually has data, and the board looked empty. */
        if (!dashMonthTouchedRef.current) setDashMonth(target);
      }
    };
    const poll = () => fetchFilters().then((f) => apply(f.months)).catch(() => {});
    poll();
    // Skip the tick while the tab is hidden — an idle/background tab shouldn't query.
    const id = setInterval(() => { if (document.hidden) return; poll(); }, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const autoSave = useCallback((type: 'sales' | 'pm', values: DeptRow) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveEOMadjustment(month, type, values)
        .then(() => toastSuccess('Adjustment saved'))
        .catch((err) => { console.error(err); toastError('Failed to save adjustment'); });
    }, 500);
  }, [month]);

  const updateSalesAdj = (field: keyof DeptRow, value: number) => {
    if (editLocked) return;
    const updated = { ...salesAdj, [field]: value };
    setSalesAdj(updated);
    autoSave('sales', updated);
  };

  const updatePmAdj = (field: keyof DeptRow, value: number) => {
    if (editLocked) return;
    const updated = { ...pmAdj, [field]: value };
    setPmAdj(updated);
    autoSave('pm', updated);
  };

  const sumRow = (row: DeptRow) => row.ranking + row.creative + row.tech + row.performance;

  const d = data?.eom || {
    recurring_sales: ZERO, one_time_sales: ZERO, sales_pia: ZERO,
    sales_refund_dispute: ZERO, sales_disputes_won: ZERO,
    pm_inventory: ZERO, pm_retention_achieved: ZERO,
    pm_recurring_upsell: ZERO, pm_one_time_upsell: ZERO,
    pm_pia: ZERO, pm_refunds_disputes: ZERO, pm_disputes_won: ZERO,
    shopify: ZERO, // [SHOPIFY 2026-10-19]
  };
  const shopifyRow = d.shopify ?? ZERO; // [SHOPIFY 2026-10-19] the table's Shopify line (ranking slot)

  /* [ONE SOURCE 2026-09-03] Every figure below comes from deriveEomTotals — the SAME function the
     Dashboard view calls for this month AND for last month. Previously this arithmetic lived inline
     here, so anything else needing it (a second month, a summary card) had to restate it and could
     drift. Do not inline it again. */
  const {
    grossNewSales, netNewSales, pmRetentionPct, grossPmSo, netPmTotal,
    grossSeoTotal, seoRefundsAdj, netSeoTotal,
  } = deriveEomTotals(d, salesAdj, pmAdj);
  // [PDF-EXPORT 2026-09-30] The PM Retention % row's Total cell, named once so the table and the
  // Download PDF read the same figure.
  const pmRetentionTotalPct = sumRow(d.pm_inventory) > 0 ? (sumRow(d.pm_retention_achieved) / sumRow(d.pm_inventory)) * 100 : 0;

  // [EOM-ROW-SPLIT 2026-08-21] Four rows now: ROW - PM and ROW - Sales sit beneath their
  // parents, holding the ROW money that used to be invisible inside PM and Sales. The server
  // SUBTRACTS that money from the parent row, so the table total is unchanged. This default is
  // only the pre-data shape; the real rows (and their order) come from the server.
  const defaultDeptRevenue = [
    { team: 'PM', gross: 0, refundDispute: 0, disputeWon: 0 },
    { team: 'Sales', gross: 0, refundDispute: 0, disputeWon: 0 },
    { team: 'ROW - PM', gross: 0, refundDispute: 0, disputeWon: 0 },
    { team: 'ROW - Sales', gross: 0, refundDispute: 0, disputeWon: 0 },
    { team: 'Shopify', gross: 0, refundDispute: 0, disputeWon: 0 }, // [SHOPIFY 2026-10-19]
  ];
  const deptRevenue = data?.deptRevenue || defaultDeptRevenue;
  // [ADJ→DISPUTE-WON 2026-07-01] The PM Adjustment entered in the EOM table flows into the
  // PM row's "Dispute Won/Recovery" here (treated as money kept, like a won dispute), so the
  // Department Wise Revenue table reflects the adjustment. Net recomputes from the adjusted
  // Dispute Won. Sales rows are unchanged (Sales adjustment is not mapped here).
  const pmAdjTotal = sumRow(pmAdj);
  const deptWithNet = deptRevenue.map((r) => {
    const disputeWon = r.team === 'PM' ? r.disputeWon + pmAdjTotal : r.disputeWon;
    return { ...r, disputeWon, net: r.gross - r.refundDispute + disputeWon };
  });
  const deptTotal = {
    gross: deptWithNet.reduce((s, r) => s + r.gross, 0),
    refundDispute: deptWithNet.reduce((s, r) => s + r.refundDispute, 0),
    disputeWon: deptWithNet.reduce((s, r) => s + r.disputeWon, 0),
    net: deptWithNet.reduce((s, r) => s + r.net, 0),
  };

  const provInventory = data?.provInventory || 0;
  // The two months the Dashboard view compares, both through the same derivation.
  /* [OVERALL BOARD 2026-09-03, user] All THREE boards, both months — six sets of figures from ONE
     function, so Overall, USA and ROW can never be derived differently. Overall is computed from
     the payload the same way the other two are, NOT by adding the other two together: a sum would
     tie to them by definition and so could never disprove anything, whereas an independent
     derivation makes "Overall = USA + ROW" a real check that either passes or fails. */
  const overallNow = monthFigures(dashData, dashSalesAdj, dashPmAdj, 'overall');
  const overallPrev = monthFigures(prevData, prevSalesAdj, prevPmAdj, 'overall');
  const figNow = monthFigures(dashData, dashSalesAdj, dashPmAdj, 'usa');
  const figPrev = monthFigures(prevData, prevSalesAdj, prevPmAdj, 'usa');
  const rowNow = monthFigures(dashData, dashSalesAdj, dashPmAdj, 'row');
  const rowPrev = monthFigures(prevData, prevSalesAdj, prevPmAdj, 'row');
  const defaultRetentionPeriods = (() => {
    const [y, mo] = month.split('-').map(Number);
    const lastDay = new Date(y, mo, 0).getDate();
    const monthShort = MONTH_SHORT_FMT.format(new Date(y, mo - 1));
    return [
      { label: `01-10 ${monthShort} ${y}`, expected: 0, achieved: 0 },
      { label: `11-20 ${monthShort} ${y}`, expected: 0, achieved: 0 },
      { label: `21-${lastDay} ${monthShort} ${y}`, expected: 0, achieved: 0 },
      { label: `Overall ${monthShort} ${y}`, expected: 0, achieved: 0 },
    ];
  })();
  const retentionPeriods = data?.retentionPeriods || defaultRetentionPeriods;

  const renderVal = (v: number) => (v === 0 ? <Dash>--</Dash> : fmtCurrency(v));
  const renderPct = (v: number) => (v === 0 ? <Dash>--</Dash> : fmtPercent(v));

  /* [PDF-EXPORT 2026-09-30, user] "Download PDF" (EOM view). Every cell is built from the SAME
     variables and formatters as the tables below, row for row: "--" where the page prints its
     dash, and an adjustment left blank stays blank. The one figure the page does not print is the
     retention table's Achieved %, which is Retention Achieved / Retention Expected for that period. */
  /* [PDF-EXPORT 2026-09-30, user] "At the time of skeleton screen the download PDF button gets faded
     out, make it just like the other one": it is no longer disabled while the month loads (it looks
     like the toggle and month picker beside it). A click during loading is remembered and the file
     downloads the moment the figures land; only a FAILED load leaves nothing to download. */
  const [pdfQueued, setPdfQueued] = useState(false);
  const onPdfClick = () => { if (loading || !data) { setPdfQueued(true); return; } downloadEomPdf(); };
  useEffect(() => {
    if (!pdfQueued || loading) return;
    setPdfQueued(false);
    if (data && !loadFailed) downloadEomPdf();
  });

  /* [EOM-XLSX 2026-10-08, user] "Download Excel" (Dashboard view): the month selected at the click,
     three tabs — Overall / USA / ROW — each a light-theme copy of its Dashboard row, built by
     dashboardSheet() from the SAME six figure sets the boards render. Like the PDF button, a click
     while the month loads is remembered and the file downloads the moment the figures land; if the
     month changes meanwhile (or the load fails) the queued download is dropped. */
  const [xlsxQueued, setXlsxQueued] = useState<string | null>(null);
  const dashReady = !dashLoading && !dashFailed && dashData != null && dashDataMonthRef.current === dashMonth;
  const downloadDashboardXlsx = () => {
    const sheets = ([
      ['Overall', overallNow, overallPrev],
      ['USA', figNow, figPrev],
      ['ROW', rowNow, rowPrev],
    ] as [string, EomMonthFigures | null, EomMonthFigures | null][])
      .filter(([, now]) => now != null)
      .map(([name, now, prev]) => dashboardSheet(name, now as EomMonthFigures, prev, dashMonth));
    if (!sheets.length) return;
    downloadXlsx(`EOM Dashboard - ${formatMonthTitle(dashMonth)}.xlsx`, buildEomDashboardXlsx({
      monthTitle: formatMonthTitle(dashMonth),
      lastMonthTitle: formatMonthTitle(addMonthsToYYYYMM(dashMonth, -1)),
      sheets,
    }));
  };
  const onXlsxClick = () => { if (!dashReady) { setXlsxQueued(dashMonth); return; } downloadDashboardXlsx(); };
  useEffect(() => {
    if (!xlsxQueued) return;
    if (xlsxQueued !== dashMonth || (!dashLoading && dashFailed)) { setXlsxQueued(null); return; }
    if (!dashReady) return;
    setXlsxQueued(null);
    downloadDashboardXlsx();
  });
  const downloadEomPdf = () => {
    if (!data) return;
    const cur = fmtCurrency;
    const DASH = '--';
    const val = (v: number) => (v === 0 ? DASH : fmtCurrency(v)); // renderVal
    const pct = (v: number) => (v === 0 ? DASH : fmtPercent(v)); // renderPct
    const adj = (v: number) => (v ? fmtCurrency(v) : ''); // AdjField when not being edited
    const all = (r: DeptRow) => [cur(r.ranking), cur(r.creative), cur(r.tech), cur(r.performance), cur(sumRow(r))];
    const rankingOnly = (r: DeptRow) => [cur(r.ranking), DASH, DASH, DASH, cur(r.ranking)];
    const adjRow = (r: DeptRow) => [adj(r.ranking), adj(r.creative), adj(r.tech), adj(r.performance), cur(sumRow(r))];
    try {
      downloadEomReport({
        month,
        eomRows: [
          { label: 'Recurring New Sales', cells: all(d.recurring_sales) },
          { label: 'One-Time New Sales', cells: all(d.one_time_sales) },
          { label: 'Gross New Sales', cells: all(grossNewSales), bold: true },
          { label: 'Sales - PIA', cells: all(d.sales_pia) },
          { label: 'Sales - Refund/Dispute', cells: [cur(d.sales_refund_dispute.ranking), DASH, DASH, DASH, cur(sumRow(d.sales_refund_dispute))] },
          { label: 'Sales - Disputes Won/Recovery', cells: [cur(d.sales_disputes_won.ranking), val(d.sales_disputes_won.creative), val(d.sales_disputes_won.tech), val(d.sales_disputes_won.performance), cur(sumRow(d.sales_disputes_won))] },
          { label: 'Sales - Adjustment', cells: adjRow(salesAdj) },
          { label: 'Net New Sales', cells: all(netNewSales), bold: true },
          { label: 'PM Inventory', cells: rankingOnly(d.pm_inventory) },
          { label: 'PM Retention Achieved', cells: all(d.pm_retention_achieved) },
          { label: 'PM Retention %', cells: [fmtPercent(pmRetentionPct.ranking), pct(pmRetentionPct.creative), pct(pmRetentionPct.tech), pct(pmRetentionPct.performance), fmtPercent(pmRetentionTotalPct)] },
          { label: 'PM Recurring Upsell', cells: all(d.pm_recurring_upsell) },
          { label: 'PM One-Time Upsell', cells: all(d.pm_one_time_upsell) },
          { label: 'Gross PM SO Total', cells: all(grossPmSo), bold: true },
          { label: 'PM - PIA', cells: all(d.pm_pia) },
          { label: 'PM - Refunds/Disputes', cells: rankingOnly(d.pm_refunds_disputes) },
          { label: 'PM - Disputes Won/Recovery', cells: rankingOnly(d.pm_disputes_won) },
          { label: 'PM - Adjustment', cells: adjRow(pmAdj) },
          { label: 'Net PM Total', cells: all(netPmTotal), bold: true },
          { label: 'Shopify', cells: rankingOnly(shopifyRow) }, // [SHOPIFY 2026-10-19]
          { label: 'Gross SEO Total', cells: all(grossSeoTotal) },
          { label: 'SEO Refunds/Disputes/Adj', cells: all(seoRefundsAdj) },
        ],
        eomTotal: { label: 'Net SEO Total', cells: all(netSeoTotal) },
        deptRows: deptWithNet.map((r) => ({ label: r.team, cells: [cur(r.gross), cur(r.refundDispute), cur(r.disputeWon), cur(r.net)] })),
        deptTotal: { label: 'Total', cells: [cur(deptTotal.gross), cur(deptTotal.refundDispute), cur(deptTotal.disputeWon), cur(deptTotal.net)] },
        provisional: cur(provInventory),
        retention: retentionPeriods.map((p) => ({
          label: fmtDayRangeLabel(p.label),
          cells: [cur(p.expected), cur(p.achieved), p.expected > 0 ? fmtPercent((p.achieved / p.expected) * 100) : DASH],
        })),
      });
    } catch (err) {
      console.error(err);
      toastError('Could not build the PDF');
    }
  };

  const maxRetention = Math.max(...retentionPeriods.map((p) => Math.max(p.expected, p.achieved)), 1);

  function fmtK(v: number): string {
    if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}K`;
    return fmtCurrency(v);
  }

  // Generate months list (last 12 months). [EFFECTIVE-MONTH 2026-07-01] Anchored on
  // the effective current month (12:30 PM IST rollover on the 1st) so months[0] — the
  // newest option — matches the default selection and the rest of the app.
  // [MONTH-FLOOR] The dropdown starts at Jan 2026 — never offer an earlier month, so
  // the 12-month window is clamped to the floor (drops Aug–Dec 2025 while it's in range).
  // [VIEW TOGGLE 2026-09-03, user] 'eom' is the default view; 'dashboard' is the second tab.
  const [view, setView] = useState<'eom' | 'dashboard'>('eom');
  // [FLUID-6 2026-10-12] The page's switch, run by the pill (EomViewToggle) as a low-priority update.
  const pickView = useCallback((v: EomView) => { setView(v); if (v === 'dashboard') setDashTouched(true); }, []);
  // [FLUID-6 2026-10-12, user] "the first time when someone comes on the tab it shows the animation for the data, and
  // then in the toggle switch change it will not show an animation": the rows rise in when the data lands (<Reveal
  // $rows> below — the first visit, a month pick), and a switch between EOM and Dashboard only slides: no row animation.

  const months: string[] = [];
  const curMonth = getCurrentMonth();
  for (let i = 0; i < 12; i++) {
    const m = addMonthsToYYYYMM(curMonth, -i);
    if (atOrAfterMinMonth(m)) months.push(m);
  }

  return (
    <Page>
      <FilterRow>
        <EomViewToggle view={view} onPick={pickView /* [FLUID-6 2026-10-12] the pill moves at the click */} />
        <MonthSelect liquid /* [LIQUID-GLASS 2026-11-02] */ deferChange /* [FLUID-8 2026-10-12] instant pick */ value={view === 'eom' ? month : dashMonth}
          onChange={(e) => {
            if (view === 'eom') { setMonth(e.target.value); return; }
            dashMonthTouchedRef.current = true;
            setDashMonth(e.target.value);
          }}>
          {months.map((m) => (
            <option key={m} value={m}>{formatMonthTitle(m)}</option>
          ))}
        </MonthSelect>
        {/* [PDF-EXPORT 2026-09-30, user] EOM view only; the Dashboard view has no PDF. */}
        {view === 'eom' && (
          <PdfButton type="button" onClick={onPdfClick} disabled={!loading && (loadFailed || !data)}
            title="Download this month's EOM tables as a PDF">
            <i className="fas fa-download" aria-hidden="true" /> Download PDF
          </PdfButton>
        )}
        {/* [EOM-XLSX 2026-10-08, user] Dashboard view only; the EOM view keeps its PDF. */}
        {view === 'dashboard' && (
          <PdfButton type="button" onClick={onXlsxClick} disabled={!dashLoading && (dashFailed || !figNow)}
            title="Download this month's dashboard (Overall, USA and ROW) as an Excel file">
            <i className="fas fa-download" aria-hidden="true" /> Download Excel
          </PdfButton>
        )}
      </FilterRow>
      <Viewport>
      {/* [PER-VIEW LOADING 2026-09-03, user] Each panel owns its own skeleton. The skeleton used
          to wrap BOTH panels, so changing the month on one side blanked the other. */}
      <Panel $active={view === 'eom'} $from="left" aria-hidden={view !== 'eom'}>
        {loading ? (
          <>
        {/* [SKELETON ALIGNMENT 2026-09-03, user] The loading state has to occupy the SAME boxes the
            real page does, or the tables jump when data lands. The two section titles used to be
            loose 14px bars where the real page uses <TitleRow> (min-height 38px + 16px margin) —
            about 26px short each, which pulled both tables upward. Reusing the real TitleRow means
            it cannot drift again. The filter row is no longer part of the skeleton at all: it
            lives above the Viewport and stays on screen while either side loads. */}
        <Grid>
          <div>
            <TitleRow><SkeletonBox $w="40%" $h="15px" /></TitleRow>
            <TableWrap>
              <TableSkeleton
                rows={10}
                columns={['Particulars', 'Ranking', 'Creative', 'Tech', 'Performance', 'Total']
                  .map((label, c) => ({ label, align: c === 0 ? 'left' : 'right' as const }))}
              />
            </TableWrap>
          </div>
          <div>
            <TitleRow><SkeletonBox $w="55%" $h="15px" /></TitleRow>
            <TableWrap>
              <TableSkeleton
                rows={6 /* [SHOPIFY 2026-10-19] PM, Sales, ROW - PM, ROW - Sales, Shopify + Total */}
                columns={['Team', 'Gross', 'Refund/Dispute', 'Dispute Won/Recovery', 'Net']
                  .map((label, c) => ({ label, align: c === 0 ? 'left' : 'right' as const }))}
              />
            </TableWrap>
            <SkeletonBox $w="60%" $h="13px" $mt="22px" />
            <SkeletonBox $h="180px" $radius="12px" $mt="14px" />
          </div>
        </Grid>
          </>
        ) : loadFailed ? (
          <LoadFailed what="EOM figures" onRetry={() => setEomReload((n) => n + 1)} />
        ) : (
        <Reveal $plain $rows /* [FLUID-4 2026-10-12] first rows rise in as the data lands */>
        {/* [MOTION-A 2026-08-20] Fades the real content in as it replaces the skeleton. */}
      <Grid>
        {/* ── LEFT: EOM Table ── */}
        <div>
          <TitleRow>
            <SectionTitle style={{ flex: 1, margin: 0 }}>EOM | <span>{formatMonthTitle(month)}</span></SectionTitle>
          </TitleRow>
          <TableWrap>
            <Table>
              <Thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Particulars</th>
                  <th>Ranking</th>
                  <th>Creative</th>
                  <th>Tech</th>
                  <th>Performance</th>
                  <th>Total</th>
                </tr>
              </Thead>
              <Tbody>
                {/* Sales Section */}
                <tr>
                  <td>Recurring New Sales</td>
                  <td>{fmtCurrency(d.recurring_sales.ranking)}</td>
                  <td>{fmtCurrency(d.recurring_sales.creative)}</td>
                  <td>{fmtCurrency(d.recurring_sales.tech)}</td>
                  <td>{fmtCurrency(d.recurring_sales.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.recurring_sales))}</td>
                </tr>
                <tr>
                  <td>One-Time New Sales</td>
                  <td>{fmtCurrency(d.one_time_sales.ranking)}</td>
                  <td>{fmtCurrency(d.one_time_sales.creative)}</td>
                  <td>{fmtCurrency(d.one_time_sales.tech)}</td>
                  <td>{fmtCurrency(d.one_time_sales.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.one_time_sales))}</td>
                </tr>
                <SectionRow>
                  <td>Gross New Sales</td>
                  <td>{fmtCurrency(grossNewSales.ranking)}</td>
                  <td>{fmtCurrency(grossNewSales.creative)}</td>
                  <td>{fmtCurrency(grossNewSales.tech)}</td>
                  <td>{fmtCurrency(grossNewSales.performance)}</td>
                  <td>{fmtCurrency(sumRow(grossNewSales))}</td>
                </SectionRow>
                <tr>
                  <td>Sales - PIA</td>
                  <td>{fmtCurrency(d.sales_pia.ranking)}</td>
                  <td>{fmtCurrency(d.sales_pia.creative)}</td>
                  <td>{fmtCurrency(d.sales_pia.tech)}</td>
                  <td>{fmtCurrency(d.sales_pia.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.sales_pia))}</td>
                </tr>
                <tr>
                  <td>Sales - Refund/Dispute</td>
                  <td>{fmtCurrency(d.sales_refund_dispute.ranking)}</td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td>{fmtCurrency(sumRow(d.sales_refund_dispute))}</td>
                </tr>
                <tr>
                  {/* [EOM-RECOVERY 2026-08-31] Both rows carry won disputes PLUS the side's
                      share of payments.recovery (split by the payment's own mapped buckets;
                      recovery with no side stays out, for the Adjustment row). */}
                  <td>Sales - Disputes Won/Recovery</td>
                  <td>{fmtCurrency(d.sales_disputes_won.ranking)}</td>
                  <td>{renderVal(d.sales_disputes_won.creative)}</td>
                  <td>{renderVal(d.sales_disputes_won.tech)}</td>
                  <td>{renderVal(d.sales_disputes_won.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.sales_disputes_won))}</td>
                </tr>
                <tr>
                  <InputCell>Sales - Adjustment</InputCell>
                  <InputCell><AdjField disabled={editLocked} value={salesAdj.ranking} onCommit={(v) => updateSalesAdj('ranking', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={salesAdj.creative} onCommit={(v) => updateSalesAdj('creative', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={salesAdj.tech} onCommit={(v) => updateSalesAdj('tech', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={salesAdj.performance} onCommit={(v) => updateSalesAdj('performance', v)} /></InputCell>
                  <td>{fmtCurrency(sumRow(salesAdj))}</td>
                </tr>
                <SectionRow>
                  <td>Net New Sales</td>
                  <td>{fmtCurrency(netNewSales.ranking)}</td>
                  <td>{fmtCurrency(netNewSales.creative)}</td>
                  <td>{fmtCurrency(netNewSales.tech)}</td>
                  <td>{fmtCurrency(netNewSales.performance)}</td>
                  <td>{fmtCurrency(sumRow(netNewSales))}</td>
                </SectionRow>

                {/* PM Section */}
                <tr>
                  <td>PM Inventory</td>
                  <td>{fmtCurrency(d.pm_inventory.ranking)}</td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td>{fmtCurrency(d.pm_inventory.ranking)}</td>
                </tr>
                <tr>
                  <td>PM Retention Achieved</td>
                  <td>{fmtCurrency(d.pm_retention_achieved.ranking)}</td>
                  <td>{fmtCurrency(d.pm_retention_achieved.creative)}</td>
                  <td>{fmtCurrency(d.pm_retention_achieved.tech)}</td>
                  <td>{fmtCurrency(d.pm_retention_achieved.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.pm_retention_achieved))}</td>
                </tr>
                <tr>
                  <td>PM Retention %</td>
                  <td>{fmtPercent(pmRetentionPct.ranking)}</td>
                  <td>{renderPct(pmRetentionPct.creative)}</td>
                  <td>{renderPct(pmRetentionPct.tech)}</td>
                  <td>{renderPct(pmRetentionPct.performance)}</td>
                  <td>{fmtPercent(pmRetentionTotalPct)}</td>
                </tr>
                <tr>
                  <td>PM Recurring Upsell</td>
                  <td>{fmtCurrency(d.pm_recurring_upsell.ranking)}</td>
                  <td>{fmtCurrency(d.pm_recurring_upsell.creative)}</td>
                  <td>{fmtCurrency(d.pm_recurring_upsell.tech)}</td>
                  <td>{fmtCurrency(d.pm_recurring_upsell.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.pm_recurring_upsell))}</td>
                </tr>
                <tr>
                  <td>PM One-Time Upsell</td>
                  <td>{fmtCurrency(d.pm_one_time_upsell.ranking)}</td>
                  <td>{fmtCurrency(d.pm_one_time_upsell.creative)}</td>
                  <td>{fmtCurrency(d.pm_one_time_upsell.tech)}</td>
                  <td>{fmtCurrency(d.pm_one_time_upsell.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.pm_one_time_upsell))}</td>
                </tr>
                <SectionRow>
                  <td>Gross PM SO Total</td>
                  <td>{fmtCurrency(grossPmSo.ranking)}</td>
                  <td>{fmtCurrency(grossPmSo.creative)}</td>
                  <td>{fmtCurrency(grossPmSo.tech)}</td>
                  <td>{fmtCurrency(grossPmSo.performance)}</td>
                  <td>{fmtCurrency(sumRow(grossPmSo))}</td>
                </SectionRow>
                <tr>
                  <td>PM - PIA</td>
                  <td>{fmtCurrency(d.pm_pia.ranking)}</td>
                  <td>{fmtCurrency(d.pm_pia.creative)}</td>
                  <td>{fmtCurrency(d.pm_pia.tech)}</td>
                  <td>{fmtCurrency(d.pm_pia.performance)}</td>
                  <td>{fmtCurrency(sumRow(d.pm_pia))}</td>
                </tr>
                <tr>
                  <td>PM - Refunds/Disputes</td>
                  <td>{fmtCurrency(d.pm_refunds_disputes.ranking)}</td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td>{fmtCurrency(d.pm_refunds_disputes.ranking)}</td>
                </tr>
                <tr>
                  <td>PM - Disputes Won/Recovery</td>
                  <td>{fmtCurrency(d.pm_disputes_won.ranking)}</td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td>{fmtCurrency(d.pm_disputes_won.ranking)}</td>
                </tr>
                <tr>
                  <InputCell>PM - Adjustment</InputCell>
                  <InputCell><AdjField disabled={editLocked} value={pmAdj.ranking} onCommit={(v) => updatePmAdj('ranking', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={pmAdj.creative} onCommit={(v) => updatePmAdj('creative', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={pmAdj.tech} onCommit={(v) => updatePmAdj('tech', v)} /></InputCell>
                  <InputCell><AdjField disabled={editLocked} value={pmAdj.performance} onCommit={(v) => updatePmAdj('performance', v)} /></InputCell>
                  <td>{fmtCurrency(sumRow(pmAdj))}</td>
                </tr>
                <SectionRow>
                  <td>Net PM Total</td>
                  <td>{fmtCurrency(netPmTotal.ranking)}</td>
                  <td>{fmtCurrency(netPmTotal.creative)}</td>
                  <td>{fmtCurrency(netPmTotal.tech)}</td>
                  <td>{fmtCurrency(netPmTotal.performance)}</td>
                  <td>{fmtCurrency(sumRow(netPmTotal))}</td>
                </SectionRow>

                {/* [SHOPIFY 2026-10-19, user] "in the eom table i want to include this thing": the month's Shopify payouts.
                    No department — the amount sits in the Ranking column like PM Inventory and the Disputes Won/Recovery
                    rows, so every Total adds up across its four columns; it is inside Gross SEO Total and Net SEO Total. */}
                <tr>
                  <td>Shopify</td>
                  <td>{fmtCurrency(shopifyRow.ranking)}</td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td><Dash>--</Dash></td>
                  <td>{fmtCurrency(shopifyRow.ranking)}</td>
                </tr>

                {/* SEO Totals */}
                <tr>
                  <td>Gross SEO Total</td>
                  <td>{fmtCurrency(grossSeoTotal.ranking)}</td>
                  <td>{fmtCurrency(grossSeoTotal.creative)}</td>
                  <td>{fmtCurrency(grossSeoTotal.tech)}</td>
                  <td>{fmtCurrency(grossSeoTotal.performance)}</td>
                  <td>{fmtCurrency(sumRow(grossSeoTotal))}</td>
                </tr>
                <tr>
                  <td>SEO Refunds/Disputes/Adj</td>
                  <td>{fmtCurrency(seoRefundsAdj.ranking)}</td>
                  <td>{fmtCurrency(seoRefundsAdj.creative)}</td>
                  <td>{fmtCurrency(seoRefundsAdj.tech)}</td>
                  <td>{fmtCurrency(seoRefundsAdj.performance)}</td>
                  <td>{fmtCurrency(sumRow(seoRefundsAdj))}</td>
                </tr>
                <TotalRow>
                  <td>Net SEO Total</td>
                  <td>{fmtCurrency(netSeoTotal.ranking)}</td>
                  <td>{fmtCurrency(netSeoTotal.creative)}</td>
                  <td>{fmtCurrency(netSeoTotal.tech)}</td>
                  <td>{fmtCurrency(netSeoTotal.performance)}</td>
                  <td>{fmtCurrency(sumRow(netSeoTotal))}</td>
                </TotalRow>
              </Tbody>
            </Table>
          </TableWrap>
        </div>

        {/* ── RIGHT: Dept Revenue + Retention ── */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <TitleRow>
            <SectionTitle style={{ flex: 1, margin: 0 }}>Department Wise Revenue | <span>{formatMonthTitle(month)}</span></SectionTitle>
          </TitleRow>
          <TableWrap>
            <Table>
              <Thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Team</th>
                  <th>Gross</th>
                  <th>Refund/Dispute</th>
                  <th>Dispute Won/Recovery</th>
                  <th>Net</th>
                </tr>
              </Thead>
              <Tbody>
                {deptWithNet.map((r) => (
                  <tr key={r.team}>
                    <td>{r.team}</td>
                    <td>{fmtCurrency(r.gross)}</td>
                    <td>{fmtCurrency(r.refundDispute)}</td>
                    <td>{fmtCurrency(r.disputeWon)}</td>
                    <td>{fmtCurrency(r.net)}</td>
                  </tr>
                ))}
                <TotalRow>
                  <td>Total</td>
                  <td>{fmtCurrency(deptTotal.gross)}</td>
                  <td>{fmtCurrency(deptTotal.refundDispute)}</td>
                  <td>{fmtCurrency(deptTotal.disputeWon)}</td>
                  <td>{fmtCurrency(deptTotal.net)}</td>
                </TotalRow>
              </Tbody>
            </Table>
          </TableWrap>

          <ProvBanner>
            Next Month Provisional Inventory - {fmtCurrency(provInventory)}
          </ProvBanner>

          {/* Retention Chart */}
          <RetentionSection>
            <RetentionTitle>PM Retention Status &ndash; 10-Day Breakdown</RetentionTitle>

            <Legend>
              <span><LegendDot $variant="neutral" />Retention Expected</span>
              <span><LegendDot $variant="green" />Retention Achieved</span>
            </Legend>

            <ChartArea>
              {retentionPeriods.map((p, i) => (
                <BarGroup key={i}>
                  <BarContainer>
                    <Bar $height={maxRetention > 0 ? Math.max((p.expected / maxRetention) * 140, 4) : 4} $variant="neutral">
                      <BarLabel>{fmtK(p.expected)}</BarLabel>
                    </Bar>
                    <Bar $height={maxRetention > 0 ? Math.max((p.achieved / maxRetention) * 140, 4) : 4} $variant="green">
                      <BarLabel>{fmtK(p.achieved)}</BarLabel>
                    </Bar>
                  </BarContainer>
                  <BarPeriod>{fmtDayRangeLabel(p.label)}</BarPeriod>
                </BarGroup>
              ))}
            </ChartArea>

            <TableWrap>
              <Table>
                <RetentionThead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Period</th>
                    {retentionPeriods.map((p, i) => (
                      <th key={i}>{fmtDayRangeLabel(p.label)}</th>
                    ))}
                  </tr>
                </RetentionThead>
                <Tbody>
                  <tr>
                    <td>Retention Expected</td>
                    {retentionPeriods.map((p, i) => (
                      <td key={i}>{fmtCurrency(p.expected)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Retention Achieved</td>
                    {retentionPeriods.map((p, i) => (
                      <td key={i}>{fmtCurrency(p.achieved)}</td>
                    ))}
                  </tr>
                  <TotalRow>
                    <td>Difference</td>
                    {retentionPeriods.map((p, i) => {
                      const diff = p.achieved - p.expected;
                      return (
                        <td key={i} style={{ color: diff < 0 ? '#dc2626' : undefined }}>
                          {fmtCurrency(diff)}
                        </td>
                      );
                    })}
                  </TotalRow>
                </Tbody>
              </Table>
            </TableWrap>
          </RetentionSection>
        </div>
      </Grid>
        </Reveal>
        )}
      </Panel>

      <Panel $active={view === 'dashboard'} $from="right" aria-hidden={view !== 'dashboard'}>
        {dashLoading ? (
          <>
            {/* Three pairs, Overall then USA then ROW — the shape the loaded view actually takes. */}
            <DashPairSkeleton />
            <DashDivide aria-hidden="true" />
            <DashPairSkeleton />
            <DashDivide aria-hidden="true" />
            <DashPairSkeleton />
          </>
        ) : dashFailed ? (
          <LoadFailed what="the EOM dashboard" onRetry={() => setDashReload((n) => n + 1)} />
        ) : (
        <Reveal $plain $rows /* [FLUID-4 2026-10-12] first rows rise in as the data lands */>
        {figNow ? (
          <>
            {/* Each side gets its board on the left and its own summary table on the right.
                [OVERALL BOARD 2026-09-03, user] Overall sits FIRST — the whole company, USA and ROW
                combined, carrying the Adjustment rows that have no ROW half to bifurcate into.
                It is the board that ties to the EOM tab's own table. */}
            {overallNow && (
              <>
                <DashSplit>
                  <DashCol>
                    <DashBoard title="EOM Report (Overall)" now={overallNow} prev={overallPrev} month={dashMonth} theme={theme} />
                  </DashCol>
                  <DashAside>
                    <SummaryTable title="Summary (Overall)" now={overallNow} prev={overallPrev} month={dashMonth} theme={theme} />
                  </DashAside>
                </DashSplit>
                <DashDivide aria-hidden="true" />
              </>
            )}
            <DashSplit>
              <DashCol>
                <DashBoard title="EOM Report (USA)" now={figNow} prev={figPrev} month={dashMonth} theme={theme} />
              </DashCol>
              <DashAside>
                <SummaryTable title="Summary (USA)" now={figNow} prev={figPrev} month={dashMonth} theme={theme} />
              </DashAside>
            </DashSplit>
            {rowNow && (
              <>
                <DashDivide aria-hidden="true" />
                <DashSplit>
                  <DashCol>
                    <DashBoard title="EOM Report (ROW)" now={rowNow} prev={rowPrev} month={dashMonth} theme={theme} />
                  </DashCol>
                  <DashAside>
                    <SummaryTable title="Summary (ROW)" now={rowNow} prev={rowPrev} month={dashMonth} theme={theme} />
                  </DashAside>
                </DashSplit>
              </>
            )}
          </>
        ) : <DashboardBlank />}
        </Reveal>
        )}
      </Panel>
      </Viewport>
    </Page>
  );
};

export default EOMPage;
